import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Fab,
  Tooltip,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MobileAvailabilityView from './MobileAvailabilityView';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  EventAvailable as SeriesIcon,
  EventBusy as CancelIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './WeeklyAvailabilityManager.css';
import weeklyAvailabilityService from '../../services/weeklyAvailabilityService';
import timeSlotService from '../../services/timeSlotService';

// Setup moment localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Create drag-and-drop enabled calendar
const DragAndDropCalendar = withDragAndDrop(Calendar);

// Helper functions to parse recurring info from structured notes
const extractSeriesIdFromNotes = (notes) => {
  if (!notes) return null;
  // New structured format
  const structuredMatch = notes.match(/SeriesId:([^|]+)/);
  if (structuredMatch) return structuredMatch[1];
  // Fallback to old format
  const oldMatch = notes.match(/Recurring Series: ([a-zA-Z0-9]+)/);
  return oldMatch ? oldMatch[1] : null;
};

const extractPatternFromNotes = (notes) => {
  if (!notes) return null;
  // New structured format
  const structuredMatch = notes.match(/Days:([^|]+)/);
  if (structuredMatch) return structuredMatch[1];
  // Fallback to old format
  const oldMatch = notes.match(/Days: ([^|]+)/);
  return oldMatch ? oldMatch[1].trim() : null;
};

const extractWeekNumberFromNotes = (notes) => {
  if (!notes) return null;
  // New structured format
  const structuredMatch = notes.match(/Week:(\d+)/);
  if (structuredMatch) return parseInt(structuredMatch[1]);
  // Fallback to old format
  const oldMatch = notes.match(/Week (\d+)\/\d+/);
  return oldMatch ? parseInt(oldMatch[1]) : null;
};

const extractTotalWeeksFromNotes = (notes) => {
  if (!notes) return null;
  // New structured format
  const structuredMatch = notes.match(/TotalWeeks:(\d+)/);
  if (structuredMatch) return parseInt(structuredMatch[1]);
  // Fallback to old format
  const oldMatch = notes.match(/Week \d+\/(\d+)/);
  return oldMatch ? parseInt(oldMatch[1]) : null;
};

const extractRecurringTypeFromNotes = (notes) => {
  if (!notes) {
    console.log('🔍 extractRecurringTypeFromNotes: No notes provided, returning null');
    return null; // Return null instead of assuming - let caller decide default
  }
  const match = notes.match(/Type:([^|]+)/);
  const result = match ? match[1] : null; // Return null if no match found
  console.log('🔍 extractRecurringTypeFromNotes:', {
    notes: notes.substring(0, 100) + '...',
    match,
    extractedType: result
  });
  return result;
};

const extractEndDateFromNotes = (notes) => {
  if (!notes) return null;
  const match = notes.match(/EndDate:([^|]+)/);
  const result = match ? match[1] : null;
  console.log('🔍 extractEndDateFromNotes:', {
    notes: notes.substring(0, 100) + '...',
    match,
    extractedEndDate: result
  });
  return result;
};

const extractRecurringInfoFromEvent = (event) => {
  if (!event?.resource) return null;
  
  console.log('🔍 extractRecurringInfoFromEvent input:', {
    eventId: event.id,
    resourceNotes: event.resource.notes,
    recurringSeriesId: event.resource.recurringSeriesId,
    recurringTotalWeeks: event.resource.recurringTotalWeeks
  });
  
  // Try to extract type from notes first
  let extractedType = extractRecurringTypeFromNotes(event.resource.notes);
  
  // If no type found in notes, make an educated guess based on available info
  if (extractedType === null) {
    if (event.resource.recurringTotalWeeks) {
      // If we have a specific total weeks count, it's likely "Number of weeks" (occurrences)
      extractedType = 'occurrences';
      console.log('🔍 No type in notes but have recurringTotalWeeks, guessing type: occurrences');
    } else {
      // Default to "Number of weeks" as it's more commonly used
      extractedType = 'occurrences';
      console.log('🔍 No type information available, defaulting to occurrences');
    }
  }
  
  console.log('🔍 FINAL EXTRACTED TYPE:', extractedType);
  
  const result = {
    seriesId: event.resource.recurringSeriesId || extractSeriesIdFromNotes(event.resource.notes),
    pattern: event.resource.recurringPattern || extractPatternFromNotes(event.resource.notes),
    weekNumber: event.resource.recurringWeekNumber || extractWeekNumberFromNotes(event.resource.notes),
    totalWeeks: event.resource.recurringTotalWeeks || extractTotalWeeksFromNotes(event.resource.notes),
    type: extractedType,
    endDate: extractEndDateFromNotes(event.resource.notes) // Extract end date from notes
  };
  
  console.log('🔍 extractRecurringInfoFromEvent result:', result);
  return result;
};

const WeeklyAvailabilityManager = () => {
  // Responsive detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px

  // State management
  const [events, setEvents] = useState([]);
  const [originalEvents, setOriginalEvents] = useState([]); // Track original state for change detection
  const [lastActionEvents, setLastActionEvents] = useState([]); // Track events before last action for undo
  const [redoEvents, setRedoEvents] = useState([]); // Track events for redo functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showRecurringOptionsDialog, setShowRecurringOptionsDialog] = useState(false);
  const [recurringAction, setRecurringAction] = useState(null); // 'single', 'series', 'remaining'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state for event editing
  const [eventForm, setEventForm] = useState({
    title: '',
    startTime: '',
    endTime: '',
    rateOverride: '',
    isRecurring: false,
    recurringType: 'weeks', // 'weeks' or 'occurrences'
    recurringEndDate: '',
    recurringOccurrences: 4,
    selectedDays: [] // Array of selected days for recurring
  });

  // Weekly template state
  const [weeklyTemplate, setWeeklyTemplate] = useState({
    name: 'My Weekly Schedule',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    schedule: []
  });
  const [customTimezone, setCustomTimezone] = useState('');

  // Load existing weekly availability on component mount
  useEffect(() => {
    loadWeeklyAvailability();
  }, []);

  const loadWeeklyAvailability = async () => {
    try {
      setLoading(true);
      
      // Load both weekly template and individual time slots (expanded range to prevent slot disappearing)
      const loadStartDate = moment().startOf('week').subtract(1, 'week').format('YYYY-MM-DD'); // Include previous week
      const loadEndDate = moment().startOf('week').add(8, 'weeks').format('YYYY-MM-DD'); // Extended to 8 weeks ahead
      
      console.log('📅 Loading TimeSlots for date range:', {
        startDate: loadStartDate,
        endDate: loadEndDate,
        currentWeek: moment().startOf('week').format('YYYY-MM-DD'),
        nextWeek: moment().startOf('week').add(1, 'week').format('YYYY-MM-DD')
      });
      
      const [templateResponse, timeSlotsResponse] = await Promise.all([
        weeklyAvailabilityService.getWeeklyTemplate(),
        timeSlotService.getMyTimeSlots({
          startDate: loadStartDate,
          endDate: loadEndDate
        })
      ]);
      
      let allEvents = [];
      let templateEvents = [];
      let individualEvents = [];
      
      // Handle weekly template
      if (templateResponse.success && templateResponse.data) {
        const { weeklyTemplate: template } = templateResponse.data;
        setWeeklyTemplate(template);
        
        // Convert weekly template to calendar events
        templateEvents = convertTemplateToEvents(template.schedule);
      }
      
      // Handle individual time slots
      console.log('🔍 TimeSlots response:', timeSlotsResponse);
      if (timeSlotsResponse.success && timeSlotsResponse.data && timeSlotsResponse.data.timeSlots) {
        console.log(`📋 Found ${timeSlotsResponse.data.timeSlots.length} individual TimeSlots in database:`, 
          timeSlotsResponse.data.timeSlots.map(slot => ({
            id: slot._id,
            date: slot.date,
            startTime: slot.startTime,
            endTime: slot.endTime,
            status: slot.status
          }))
        );
        individualEvents = convertTimeSlotsToEvents(timeSlotsResponse.data.timeSlots);
        console.log(`✅ Converted to ${individualEvents.length} calendar events:`, individualEvents.map(e => ({
          id: e.id,
          title: e.title,
          start: e.start,
          end: e.end
        })));
      } else {
        console.log('⚠️ No TimeSlots found in database response');
      }
      
      // CRITICAL RULE: After save, NO events should show as template (blue)
      // ALL saved slots should be individual TimeSlots (green)
      
      console.log('🔍 === DEEP DEBUG EVENT CATEGORIZATION ===');
      console.log('Template events:', templateEvents.map(e => ({
        id: e.id,
        title: e.title,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual
      })));
      console.log('Individual events:', individualEvents.map(e => ({
        id: e.id,
        title: e.title,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual,
        isRecurring: e.resource?.isRecurring,
        recurringGroup: e.resource?.recurringGroup
      })));
      
      // CRITICAL DECISION: Individual TimeSlots ALWAYS take precedence over template events
      // This ensures saved slots show as GREEN, not blue templates
      console.log('🎯 === CRITICAL DISPLAY DECISION LOGIC ===');
      console.log(`Individual events available: ${individualEvents.length}`);
      console.log(`Template events available: ${templateEvents.length}`);
      
      if (individualEvents.length > 0) {
        allEvents = individualEvents;
        console.log('✅ DECISION: Using individual TimeSlots - ALL MUST show as GREEN');
        
        // Critical verification: ensure all individual events are properly marked
        allEvents.forEach((event, index) => {
          if (event.resource?.isTemplate) {
            console.error(`❌ CRITICAL ERROR: Individual event ${index} incorrectly marked as template:`, event);
          }
          if (!event.resource?.isIndividual) {
            console.error(`❌ CRITICAL ERROR: Individual event ${index} not marked as individual:`, event);
          }
        });
        
        const wronglyMarked = allEvents.filter(e => e.resource?.isTemplate && !e.resource?.isIndividual);
        if (wronglyMarked.length > 0) {
          console.error('❌ FOUND EVENTS WRONGLY MARKED AS TEMPLATE:', wronglyMarked);
          // Fix the wrongly marked events
          wronglyMarked.forEach(event => {
            console.log(`🔧 FIXING: Correcting event ${event.id} to be individual`);
            event.resource.isTemplate = false;
            event.resource.isIndividual = true;
          });
        }
        
      } else if (templateEvents.length > 0) {
        allEvents = templateEvents;
        console.log('✅ DECISION: Using template events - should show as BLUE DASHED (patterns only)');
        console.warn('⚠️ WARNING: No individual TimeSlots found, falling back to template display');
      } else {
        allEvents = [];
        console.log('⚠️ DECISION: No events to display (neither individual nor template)');
      }
      
      console.log('🎯 ==========================================');
      
      console.log(`📊 Event summary:
        - Template events available: ${templateEvents.length}
        - Individual events available: ${individualEvents.length}  
        - Total events displayed: ${allEvents.length}
        - Display mode: ${individualEvents.length > 0 ? 'Individual TimeSlots (ALL GREEN)' : 'Template Events (BLUE DASHED)'}
        - Events showing as template: ${allEvents.filter(e => e.resource?.isTemplate).length}
        - Events showing as individual: ${allEvents.filter(e => e.resource?.isIndividual).length}`);
      
      if (allEvents.length === 0) {
        console.log('⚠️ No events to display');
      }
      
      console.log(`📅 Final events for calendar (${allEvents.length} total):`, allEvents.map(e => ({
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual,
        shouldBeGreen: e.resource?.isIndividual,
        resource: e.resource
      })));
      console.log('=======================================');
      
      setEvents(allEvents);
      setOriginalEvents([...allEvents]); // Store copy for change tracking
      setHasUnsavedChanges(false); // Reset change tracking after load
    } catch (error) {
      console.error('Error loading weekly availability:', error);
      setError('Failed to load weekly availability');
    } finally {
      setLoading(false);
    }
  };

  // Convert weekly template to calendar events for display
  const convertTemplateToEvents = (schedule) => {
    if (!schedule || schedule.length === 0) {
      console.log('📋 No template schedule to convert');
      return [];
    }
    
    console.log('🔍 === CONVERTING TEMPLATE TO EVENTS ===');
    console.log('Template schedule:', schedule);
    
    const events = [];
    const today = moment().startOf('week'); // Start of current week

    schedule.forEach((slot, index) => {
      const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(slot.day.toLowerCase());
      if (dayIndex === -1) {
        console.warn(`⚠️ Invalid day in template: ${slot.day}`);
        return;
      }

      const eventDate = today.clone().add(dayIndex, 'days');
      const [startHour, startMin] = slot.startTime.split(':').map(Number);
      const [endHour, endMin] = slot.endTime.split(':').map(Number);

      const start = eventDate.clone().hour(startHour).minute(startMin).toDate();
      const end = eventDate.clone().hour(endHour).minute(endMin).toDate();

      const templateEvent = {
        id: `template-${slot.day}-${index}`,
        title: `Template Pattern (${slot.startTime} - ${slot.endTime})`,
        start,
        end,
        resource: {
          day: slot.day,
          startTime: slot.startTime,
          endTime: slot.endTime,
          rateOverride: slot.rateOverride,
          originalIndex: index,
          isTemplate: true,      // CRITICAL: Mark as template
          isIndividual: false    // CRITICAL: Not individual
        }
      };
      
      console.log(`📋 Created template event: ${templateEvent.id} - SHOULD BE BLUE DASHED`);
      events.push(templateEvent);
    });

    console.log(`✅ Template conversion complete: ${events.length} template events (ALL BLUE DASHED)`);
    console.log('=========================================');
    return events;
  };

  // Convert individual TimeSlots to calendar events for display
  /**
   * Subtracts booked slots from available time blocks
   * - Available blocks are kept INTACT (not partitioned) - displayed as solid green blocks
   * - Booked slots are partitioned into 30-min intervals and displayed as red blocks
   * - Available fragments < 30 min are removed
   * - Booked slots completely remove their time from available blocks
   */
  const partitionAndSubtractBookedSlots = (timeSlots) => {
    console.log('✂️ === PARTITIONING AND SUBTRACTING BOOKED SLOTS ===');
    console.log(`Processing ${timeSlots.length} time slots`);

    // Group slots by date
    const slotsByDate = {};
    timeSlots.forEach(slot => {
      const dateKey = slot.date;
      if (!slotsByDate[dateKey]) {
        slotsByDate[dateKey] = { available: [], booked: [] };
      }

      if (slot.status === 'booked') {
        slotsByDate[dateKey].booked.push(slot);
      } else {
        slotsByDate[dateKey].available.push(slot);
      }
    });

    const resultSlots = [];

    // Process each date
    Object.keys(slotsByDate).forEach(dateKey => {
      const { available, booked } = slotsByDate[dateKey];

      console.log(`📅 Processing ${dateKey}: ${available.length} available, ${booked.length} booked`);

      // Add all booked slots (partitioned into 30-min blocks)
      booked.forEach(bookedSlot => {
        const slotDate = moment(bookedSlot.date);
        const [startHour, startMin] = bookedSlot.startTime.split(':').map(Number);
        const [endHour, endMin] = bookedSlot.endTime.split(':').map(Number);

        const slotStart = slotDate.clone().hour(startHour).minute(startMin);
        const slotEnd = slotDate.clone().hour(endHour).minute(endMin);

        // Partition booked slot into 30-min blocks
        const durationMinutes = slotEnd.diff(slotStart, 'minutes');
        const numPartitions = Math.ceil(durationMinutes / 30);

        for (let i = 0; i < numPartitions; i++) {
          const partitionStart = slotStart.clone().add(i * 30, 'minutes');
          const partitionEnd = moment.min(partitionStart.clone().add(30, 'minutes'), slotEnd);

          resultSlots.push({
            ...bookedSlot,
            startTime: partitionStart.format('HH:mm'),
            endTime: partitionEnd.format('HH:mm'),
            _id: `${bookedSlot._id}-partition-${i}`,
            _isPartitioned: true,
            _originalId: bookedSlot._id
          });
        }
      });

      // Process available slots: subtract booked times and partition
      available.forEach(availableSlot => {
        const slotDate = moment(availableSlot.date);
        const [startHour, startMin] = availableSlot.startTime.split(':').map(Number);
        const [endHour, endMin] = availableSlot.endTime.split(':').map(Number);

        const slotStart = slotDate.clone().hour(startHour).minute(startMin);
        const slotEnd = slotDate.clone().hour(endHour).minute(endMin);

        // Find all booked slots that overlap with this available slot
        const overlappingBooked = booked.filter(bookedSlot => {
          const [bStartHour, bStartMin] = bookedSlot.startTime.split(':').map(Number);
          const [bEndHour, bEndMin] = bookedSlot.endTime.split(':').map(Number);

          const bookedStart = slotDate.clone().hour(bStartHour).minute(bStartMin);
          const bookedEnd = slotDate.clone().hour(bEndHour).minute(bEndMin);

          // Check for overlap
          return bookedStart.isBefore(slotEnd) && bookedEnd.isAfter(slotStart);
        });

        if (overlappingBooked.length === 0) {
          // No overlap, keep the available block INTACT (no partitioning)
          console.log(`  ✅ Keeping available block intact: ${availableSlot.startTime}-${availableSlot.endTime}`);
          resultSlots.push(availableSlot);
        } else {
          // Has overlaps, need to subtract booked times
          console.log(`  ✂️ Subtracting ${overlappingBooked.length} booked slots from available block ${availableSlot.startTime}-${availableSlot.endTime}`);

          // Create array of time ranges to exclude (booked times)
          const excludedRanges = overlappingBooked.map(bookedSlot => {
            const [bStartHour, bStartMin] = bookedSlot.startTime.split(':').map(Number);
            const [bEndHour, bEndMin] = bookedSlot.endTime.split(':').map(Number);

            return {
              start: slotDate.clone().hour(bStartHour).minute(bStartMin),
              end: slotDate.clone().hour(bEndHour).minute(bEndMin)
            };
          });

          // Sort excluded ranges by start time
          excludedRanges.sort((a, b) => a.start.diff(b.start));

          // Find available time ranges (gaps between booked slots)
          const availableRanges = [];
          let currentStart = slotStart.clone();

          excludedRanges.forEach(excluded => {
            // If there's a gap before this excluded range
            if (currentStart.isBefore(excluded.start)) {
              availableRanges.push({
                start: currentStart.clone(),
                end: moment.min(excluded.start, slotEnd)
              });
            }
            // Move current start to after this excluded range
            currentStart = moment.max(currentStart, excluded.end);
          });

          // Add remaining time after last excluded range
          if (currentStart.isBefore(slotEnd)) {
            availableRanges.push({
              start: currentStart,
              end: slotEnd
            });
          }

          // Keep each available range INTACT (no partitioning), discarding fragments < 30 min
          availableRanges.forEach((range, rangeIdx) => {
            const durationMinutes = range.end.diff(range.start, 'minutes');

            // Only keep ranges that are at least 30 minutes
            if (durationMinutes >= 30) {
              // Keep the entire range as one solid available block
              resultSlots.push({
                ...availableSlot,
                startTime: range.start.format('HH:mm'),
                endTime: range.end.format('HH:mm'),
                _id: `${availableSlot._id}-range${rangeIdx}`,
                _isPartitioned: false, // Not partitioned, kept intact
                _originalId: availableSlot._id
              });

              console.log(`    ✅ Kept intact available block: ${range.start.format('HH:mm')}-${range.end.format('HH:mm')} (${durationMinutes} min)`);
            } else {
              console.log(`    ⏭️ Skipping fragment ${range.start.format('HH:mm')}-${range.end.format('HH:mm')} (${durationMinutes} min < 30 min)`);
            }
          });
        }
      });
    });

    console.log(`✅ Partitioning complete: ${resultSlots.length} partitioned slots (from ${timeSlots.length} original)`);
    console.log('===========================================');
    return resultSlots;
  };

  const convertTimeSlotsToEvents = (timeSlots) => {
    console.log('🔄 === CONVERTING TIMESLOTS TO EVENTS ===');
    console.log(`Converting ${timeSlots.length} TimeSlots to calendar events`);

    // First, partition and subtract booked slots from available blocks
    const partitionedSlots = partitionAndSubtractBookedSlots(timeSlots);

    const events = partitionedSlots.map(slot => {
      const slotDate = moment(slot.date);
      const [startHour, startMin] = slot.startTime.split(':').map(Number);
      const [endHour, endMin] = slot.endTime.split(':').map(Number);

      const start = slotDate.clone().hour(startHour).minute(startMin).toDate();
      const end = slotDate.clone().hour(endHour).minute(endMin).toDate();

      // Check if this slot is part of a recurring series
      const isRecurringSlot = (slot.isRecurring && slot.recurringSeriesId) ||
                             (slot.notes && (slot.notes.startsWith('RECURRING|') || slot.notes.includes('Recurring Series:')));
      const isFromTemplate = slot.generatedFrom && slot.generatedFrom.templateId;

      let title;
      // Show only time, color indicates status (green = available, red = booked)
      const timeDisplay = `${slot.startTime} - ${slot.endTime}`;

      if (isRecurringSlot) {
        // For recurring slots, show recurring icon with time
        const weekNumber = extractWeekNumberFromNotes(slot.notes);
        const totalWeeks = extractTotalWeeksFromNotes(slot.notes);
        title = `🔁 ${timeDisplay}`;
      } else if (isFromTemplate) {
        title = `🔁 ${timeDisplay}`;
      } else {
        // Just show the time - color will indicate available/booked status
        title = timeDisplay;
      }

      const event = {
        id: `individual-${slot._id}`,
        title,
        start,
        end,
        resource: {
          day: slotDate.format('dddd').toLowerCase(),
          startTime: slot.startTime,
          endTime: slot.endTime,
          rateOverride: slot.rateOverride,
          timeSlotId: slot._originalId || slot._id,  // Use original ID for partitioned slots
          isIndividual: true,        // CRITICAL: All TimeSlots are individual
          isTemplate: false,         // CRITICAL: TimeSlots are NOT templates
          status: slot.status,
          notes: slot.notes,         // CRITICAL: Preserve notes from backend
          isRecurring: isRecurringSlot || isFromTemplate, // Mark recurring slots
          recurringGroup: isRecurringSlot ? (slot.recurringSeriesId || extractSeriesIdFromNotes(slot.notes)) : (isFromTemplate ? slot.generatedFrom.templateId : null),
          // NEW: Recurring series metadata (with fallback to notes parsing)
          recurringSeriesId: slot.recurringSeriesId || extractSeriesIdFromNotes(slot.notes),
          recurringPattern: slot.recurringPattern || extractPatternFromNotes(slot.notes),
          recurringWeekNumber: slot.recurringWeekNumber || extractWeekNumberFromNotes(slot.notes),
          recurringTotalWeeks: slot.recurringTotalWeeks || extractTotalWeeksFromNotes(slot.notes),
          recurringSeriesInfo: slot.recurringSeriesInfo
        }
      };

      console.log(`${slot.status === 'booked' ? '🔴' : '🟢'} Created event: ${event.id}`, {
        isIndividual: event.resource.isIndividual,
        isTemplate: event.resource.isTemplate,
        status: event.resource.status,
        isFromTemplate
      });

      return event;
    });

    console.log(`✅ TimeSlot conversion complete: ${events.length} calendar events`);
    console.log(`   - Available (green): ${events.filter(e => e.resource.status !== 'booked').length}`);
    console.log(`   - Booked (red): ${events.filter(e => e.resource.status === 'booked').length}`);
    console.log('===========================================');
    return events;
  };

  // Handle selecting a time slot by clicking and dragging
  const handleSelectSlot = ({ start, end, slots, action }) => {
    console.log('🎯 handleSelectSlot called:', {
      start: moment(start).format('ddd HH:mm'),
      end: moment(end).format('ddd HH:mm'),
      slotsCount: slots?.length,
      action
    });

    // Check if selection spans multiple days (horizontal drag)
    const startDay = moment(start).startOf('day');
    const endDay = moment(end).startOf('day');
    const daysDiff = endDay.diff(startDay, 'days');

    console.log(`📊 Selection analysis: startDay=${startDay.format('ddd')}, endDay=${endDay.format('ddd')}, daysDiff=${daysDiff}`);

    // Multi-day selection (horizontal drag across days)
    if (daysDiff > 0 && slots && slots.length > 1) {
      console.log(`📅 Multi-day selection detected: ${daysDiff + 1} days, ${slots.length} slots`);

      // Get the time range from the first slot
      const startTime = moment(start).format('HH:mm');
      const firstSlotEnd = moment(slots[0]).add(30, 'minutes'); // Default 30 min duration
      const endTime = firstSlotEnd.format('HH:mm');

      // Save current state for undo
      setLastActionEvents([...events]);

      // Create events for each day in the selection
      const newEvents = [];
      const selectedDays = [];

      slots.forEach((slot, index) => {
        const slotDate = moment(slot);
        const slotDayName = slotDate.format('dddd').toLowerCase();

        // Only create one event per day (avoid duplicates for same day)
        if (!selectedDays.includes(slotDayName)) {
          selectedDays.push(slotDayName);

          const slotStart = slotDate.clone().hour(moment(start).hour()).minute(moment(start).minute());
          const slotEnd = slotStart.clone().add(30, 'minutes');

          const newEvent = {
            id: `new-${Date.now()}-${index}`,
            title: `${startTime} - ${endTime}`,
            start: slotStart.toDate(),
            end: slotEnd.toDate(),
            resource: {
              day: slotDayName,
              startTime,
              endTime,
              rateOverride: null,
              isNew: true,
              isMultiDayGroup: true // Mark as part of multi-day selection
            }
          };
          newEvents.push(newEvent);
        }
      });

      setEvents(prev => [...prev, ...newEvents]);

      // Open dialog with all selected days
      setSelectedEvent(newEvents[0]); // Use first event as reference
      setEventForm({
        title: '',
        startTime,
        endTime,
        rateOverride: '',
        isRecurring: false,
        recurringType: weeklyTemplate.recurringInfo?.type || 'weeks',
        recurringEndDate: weeklyTemplate.recurringInfo?.endDate || '',
        recurringOccurrences: weeklyTemplate.recurringInfo?.occurrences || 4,
        selectedDays: selectedDays // Pre-select all dragged days
      });
      setIsEditing(true);
      setShowEventDialog(true);

      setSuccess(`Created slots for ${selectedDays.length} days: ${selectedDays.join(', ')}`);
      return;
    }

    // Single day selection (original behavior)
    const dayName = moment(start).format('dddd').toLowerCase();
    const startTime = moment(start).format('HH:mm');

    // Calculate duration and enforce 30-minute default for single clicks
    let duration = moment(end).diff(moment(start), 'minutes');
    let adjustedEnd = moment(end);

    // If duration is less than 30 minutes (likely a single click), make it 30 minutes
    if (duration < 30) {
      adjustedEnd = moment(start).add(30, 'minutes');
      duration = 30;
      console.log(`📏 Adjusted slot duration from ${duration} to 30 minutes for single click`);
    }

    const endTime = adjustedEnd.format('HH:mm');

    // Validate minimum duration (15 minutes, but we default to 30)
    if (duration < 15) {
      setError('Minimum time slot duration is 15 minutes');
      return;
    }

    // Save current state for undo
    setLastActionEvents([...events]);

    // Create new event
    const newEvent = {
      id: `new-${Date.now()}`,
      title: `${startTime} - ${endTime}`,
      start,
      end: adjustedEnd.toDate(),
      resource: {
        day: dayName,
        startTime,
        endTime,
        rateOverride: null,
        isNew: true
      }
    };

    setEvents(prev => [...prev, newEvent]);
    setSelectedEvent(newEvent);
    setEventForm({
      title: '',
      startTime,
      endTime,
      rateOverride: '',
      isRecurring: false,
      recurringType: weeklyTemplate.recurringInfo?.type || 'weeks',
      recurringEndDate: weeklyTemplate.recurringInfo?.endDate || '',
      recurringOccurrences: weeklyTemplate.recurringInfo?.occurrences || 4,
      selectedDays: [dayName]
    });
    setIsEditing(true);
    setShowEventDialog(true);
  };

  // Handle clicking on an existing event
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    
    // Check if this is a recurring event
    const isRecurringEvent = event.resource.isRecurring || event.resource.recurringGroup;
    
    if (isRecurringEvent) {
      // Show recurring options dialog first
      setShowRecurringOptionsDialog(true);
    } else {
      // Show regular event dialog for single events
      setEventForm({
        title: '',
        startTime: event.resource.startTime,
        endTime: event.resource.endTime,
        rateOverride: event.resource.rateOverride || '',
        isRecurring: false,
        recurringType: weeklyTemplate.recurringInfo?.type || 'weeks',
        recurringEndDate: weeklyTemplate.recurringInfo?.endDate || '',
        recurringOccurrences: weeklyTemplate.recurringInfo?.occurrences || eventForm.recurringOccurrences || 4, // Restore from template or preserve current
        selectedDays: [event.resource.day] // Default to the event's day
      });
      setIsEditing(false);
      setShowEventDialog(true);
    }
  };

  // Handle event form submission
  const handleSaveEvent = () => {
    if (!selectedEvent) return;

    // Check if this is a recurring action
    if (recurringAction === 'single') {
      handleSingleEventEdit();
      return;
    } else if (recurringAction === 'series') {
      handleSeriesEdit();
      return;
    }

    console.log('🔍 HANDLE SAVE EVENT ROUTING:', {
      isRecurring: eventForm.isRecurring,
      recurringType: eventForm.recurringType,
      selectedDaysCount: eventForm.selectedDays.length,
      willCallCreateRecurring: eventForm.isRecurring
    });

    if (eventForm.isRecurring) {
      // Handle creating new recurring slots
      handleCreateRecurringSlots();
    } else if (eventForm.selectedDays.length > 1) {
      // Handle multi-day non-recurring slot creation
      console.log('📅 Creating multi-day non-recurring slots for:', eventForm.selectedDays);

      // Remove the current event being edited (if it exists and is new)
      if (selectedEvent.resource?.isNew) {
        setEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
      }

      // Create new events for each selected day
      const newEvents = eventForm.selectedDays.map((dayName, index) => {
        // Get the start of the current week
        const weekStart = moment().startOf('week');
        const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(dayName.toLowerCase());
        const eventDate = weekStart.clone().day(dayIndex);

        // Parse times
        const [startHour, startMin] = eventForm.startTime.split(':').map(Number);
        const [endHour, endMin] = eventForm.endTime.split(':').map(Number);

        const eventStart = eventDate.clone().hour(startHour).minute(startMin);
        const eventEnd = eventDate.clone().hour(endHour).minute(endMin);

        return {
          id: `new-multiday-${Date.now()}-${index}`,
          title: `${eventForm.startTime} - ${eventForm.endTime}`,
          start: eventStart.toDate(),
          end: eventEnd.toDate(),
          resource: {
            day: dayName.toLowerCase(),
            startTime: eventForm.startTime,
            endTime: eventForm.endTime,
            rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null,
            isNew: true,
            isTemplate: true, // Mark as template for saving
            isMultiDayGroup: true
          }
        };
      });

      // Add all new events
      setEvents(prev => [...prev, ...newEvents]);

      setShowEventDialog(false);
      setSelectedEvent(null);
      setSuccess(`Created ${newEvents.length} time slots for ${eventForm.selectedDays.join(', ')}`);
    } else {
      // Handle regular single slot update
      const updatedEvent = {
        ...selectedEvent,
        title: `${eventForm.startTime} - ${eventForm.endTime}`, // Show time
        resource: {
          ...selectedEvent.resource,
          startTime: eventForm.startTime,
          endTime: eventForm.endTime,
          rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null
        }
      };

      // Update the event in the events array
      setEvents(prev => prev.map(event =>
        event.id === selectedEvent.id ? updatedEvent : event
      ));

      setShowEventDialog(false);
      setSelectedEvent(null);
      setSuccess('Time slot updated successfully');
    }
  };

  // COMPLETELY OVERHAULED: Direct recurring slot creation
  const handleCreateRecurringSlots = async () => {
    console.log('🚀 🚀 🚀 HANDLE CREATE RECURRING SLOTS CALLED 🚀 🚀 🚀');
    if (!selectedEvent) return;

    if (eventForm.selectedDays.length === 0) {
      setError('Please select at least one day of the week');
      return;
    }

    console.log('🚀 === NEW RECURRING LOGIC: DIRECT SLOT CREATION ===');
    console.log('🔍 CREATION FORM DATA:', {
      selectedDays: eventForm.selectedDays,
      startTime: eventForm.startTime,
      endTime: eventForm.endTime,
      recurringType: eventForm.recurringType,
      recurringOccurrences: eventForm.recurringOccurrences,
      recurringEndDate: eventForm.recurringEndDate,
      rateOverride: eventForm.rateOverride,
      isRecurring: eventForm.isRecurring
    });

    // CRITICAL VALIDATION: Check all required fields
    if (!eventForm.selectedDays || !Array.isArray(eventForm.selectedDays) || eventForm.selectedDays.length === 0) {
      console.error('❌ CRITICAL: selectedDays is empty or invalid:', eventForm.selectedDays);
      setError('No days selected for recurring event');
      setLoading(false);
      return;
    }

    if (!eventForm.startTime || !eventForm.endTime) {
      console.error('❌ CRITICAL: startTime or endTime is missing:', {
        startTime: eventForm.startTime,
        endTime: eventForm.endTime
      });
      setError('Start time and end time are required');
      setLoading(false);
      return;
    }

    if (!eventForm.recurringOccurrences || eventForm.recurringOccurrences < 1) {
      console.error('❌ CRITICAL: recurringOccurrences is invalid:', eventForm.recurringOccurrences);
      setError('Number of weeks must be at least 1');
      setLoading(false);
      return;
    }

    console.log('✅ Form validation passed');
    console.log('🔍 Selected days details:', eventForm.selectedDays.map(day => ({
      original: day,
      type: typeof day,
      length: day.length,
      toLowerCase: day.toLowerCase(),
      dayIndex: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase())
    })));

    try {
      setLoading(true);
      
      // FIRST: Clean up any existing slots that match this pattern
      console.log('🗑️ CLEANUP: Removing existing slots that match this pattern...');
      const cleanupResponse = await timeSlotService.getMyTimeSlots({
        startDate: moment().subtract(1, 'week').format('YYYY-MM-DD'),
        endDate: moment().add(8, 'weeks').format('YYYY-MM-DD')
      });
      
      if (cleanupResponse.success && cleanupResponse.data?.timeSlots) {
        const existingSlots = cleanupResponse.data.timeSlots;
        const slotsToDelete = existingSlots.filter(slot => {
          // ENHANCED: Check for existing series using multiple methods
          const selectedSeriesId = selectedEvent?.resource?.recurringSeriesId || 
                                  extractSeriesIdFromNotes(selectedEvent?.resource?.notes);
          
          // Pattern 1: Direct series ID match (both new and old formats)
          if (selectedSeriesId && (slot.recurringSeriesId === selectedSeriesId || 
                                  (slot.notes && (slot.notes.includes(`SeriesId:${selectedSeriesId}`) || 
                                                 slot.notes.includes(`Recurring Series: ${selectedSeriesId}`))))) {
            console.log(`🗑️ Deleting by series ID: ${slot._id} (series: ${selectedSeriesId})`);
            return true;
          }
          
          // Pattern 2: Match by pattern for safety
          const matchesPattern = eventForm.selectedDays.some(dayName => {
            const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(dayName.toLowerCase());
            const slotDayIndex = moment(slot.date).day();
            return dayIndex === slotDayIndex && 
                   slot.startTime === eventForm.startTime && 
                   slot.endTime === eventForm.endTime &&
                   slot.status === 'available';
          });
          
          if (matchesPattern) {
            console.log(`🗑️ Deleting by pattern match: ${slot._id}`);
            return true;
          }
          
          return false;
        });
        
        console.log(`🗑️ Found ${slotsToDelete.length} existing slots to delete:`, slotsToDelete.map(s => ({
          id: s._id,
          date: s.date,
          time: s.startTime + '-' + s.endTime
        })));
        
        for (const slot of slotsToDelete) {
          try {
            await timeSlotService.deleteTimeSlot(slot._id);
            console.log(`✅ Deleted existing slot: ${slot.date} ${slot.startTime}-${slot.endTime}`);
          } catch (deleteError) {
            console.error(`❌ Failed to delete slot ${slot._id}:`, deleteError);
          }
        }
      }
      
      // Calculate exact dates we need to create slots for
      let weeks;
      
      console.log('🧮 WEEK CALCULATION DEBUG:', {
        recurringType: eventForm.recurringType,
        recurringEndDate: eventForm.recurringEndDate,
        typeIsWeeks: eventForm.recurringType === 'weeks',
        hasEndDate: !!eventForm.recurringEndDate,
        shouldUseEndDate: eventForm.recurringType === 'weeks' && eventForm.recurringEndDate,
        willTakeEndDateBranch: (eventForm.recurringType === 'weeks' && eventForm.recurringEndDate) ? 'YES' : 'NO'
      });
      
      if (eventForm.recurringType === 'weeks' && eventForm.recurringEndDate) {
        // Calculate weeks from current date to end date
        const startOfWeek = moment().startOf('week');
        const endDate = moment(eventForm.recurringEndDate);
        
        // Calculate the number of weeks, but don't add extra
        const weekDiff = endDate.diff(startOfWeek, 'weeks', true);
        weeks = Math.floor(weekDiff) + 1; // Use floor instead of ceil, and only add 1 for current week
        
        // Additional validation: make sure we don't create slots beyond the end date
        const lastWeekStart = startOfWeek.clone().add(weeks - 1, 'weeks');
        const lastWeekEnd = lastWeekStart.clone().endOf('week');
        
        console.log('📅 UNTIL END DATE calculation:', {
          startOfWeek: startOfWeek.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          weekDiff: weekDiff,
          calculatedWeeks: weeks,
          lastWeekStart: lastWeekStart.format('YYYY-MM-DD'),
          lastWeekEnd: lastWeekEnd.format('YYYY-MM-DD'),
          endDateIsAfterLastWeekStart: endDate.isAfter(lastWeekStart) || endDate.isSame(lastWeekStart),
          recurringEndDate: eventForm.recurringEndDate
        });
        
        // If the end date is before the start of the last calculated week, reduce weeks by 1
        if (endDate.isBefore(lastWeekStart)) {
          weeks = weeks - 1;
          console.log('📅 ADJUSTED: End date is before last week start, reducing to:', weeks);
        }
      } else {
        // Use specified number of weeks (occurrences)
        weeks = parseInt(eventForm.recurringOccurrences) || 1;
        console.log('📅 NUMBER OF WEEKS calculation:', {
          recurringOccurrences: eventForm.recurringOccurrences,
          calculatedWeeks: weeks
        });
      }
      const slotsToCreate = [];
      
      // Generate unique series ID for this recurring group
      const seriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const seriesInfo = {
        id: seriesId,
        pattern: eventForm.selectedDays.join(','),
        startTime: eventForm.startTime,
        endTime: eventForm.endTime,
        weeks: weeks,
        createdAt: new Date().toISOString()
      };
      
      console.log('🔗 SERIES INFO:', seriesInfo);
      
      console.log('🔄 Starting slot generation loop...');
      console.log('Loop parameters:', { weeks, selectedDays: eventForm.selectedDays });
      
      for (let week = 0; week < weeks; week++) {
        const weekStart = moment().startOf('week').add(week, 'weeks');
        console.log(`📅 Week ${week + 1}/${weeks}: weekStart = ${weekStart.format('YYYY-MM-DD dddd')}`);
        
        eventForm.selectedDays.forEach((dayName, dayIdx) => {
          console.log(`  🗓️ Processing day ${dayIdx + 1}: "${dayName}"`);
          const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(dayName.toLowerCase());
          console.log(`  📍 dayIndex for "${dayName}": ${dayIndex}`);
          
          if (dayIndex === -1) {
            console.error(`  ❌ Invalid day name: "${dayName}"`);
            return;
          }
          
          const slotDate = weekStart.clone().add(dayIndex, 'days');
          console.log(`  📆 Slot date: ${slotDate.format('YYYY-MM-DD dddd')}`);
          
          // CRITICAL: For "Until end date" series, don't create slots after the end date
          if (eventForm.recurringType === 'weeks' && eventForm.recurringEndDate) {
            const endDate = moment(eventForm.recurringEndDate);
            if (slotDate.isAfter(endDate)) {
              console.log(`  ⏭️ SKIPPING: Slot date ${slotDate.format('YYYY-MM-DD')} is after end date ${endDate.format('YYYY-MM-DD')}`);
              return;
            }
          }
          
          // ENHANCED TIMESLOT: With structured recurring metadata in notes
          let notesData = `RECURRING|SeriesId:${seriesId}|Week:${week + 1}|TotalWeeks:${weeks}|Days:${eventForm.selectedDays.join(',')}|Type:${eventForm.recurringType}`;
          
          // Add end date to notes if using "Until end date" option
          if (eventForm.recurringType === 'weeks' && eventForm.recurringEndDate) {
            notesData += `|EndDate:${eventForm.recurringEndDate}`;
            console.log('📝 Added end date to notes:', eventForm.recurringEndDate);
          }
          
          console.log('📝 FINAL NOTES DATA:', notesData);
          
          const slotToCreate = {
            date: slotDate.format('YYYY-MM-DD'),
            startTime: eventForm.startTime,
            endTime: eventForm.endTime,
            notes: notesData,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          };
          
          // Only add rate override if specified
          if (eventForm.rateOverride && parseFloat(eventForm.rateOverride) > 0) {
            slotToCreate.rateOverride = parseFloat(eventForm.rateOverride);
          }
          
          console.log(`  ➕ Adding slot:`, slotToCreate);
          slotsToCreate.push(slotToCreate);
        });
      }
      
      console.log(`🔄 Generation loop complete. Total slots to create: ${slotsToCreate.length}`);
      
      console.log(`📅 WILL CREATE ${slotsToCreate.length} slots:`, slotsToCreate.map(s => ({
        date: s.date,
        time: s.startTime + '-' + s.endTime,
        dayOfWeek: moment(s.date).format('dddd'),
        weekNumber: s.recurringWeekNumber
      })));
      
      if (slotsToCreate.length === 0) {
        console.error('❌ NO SLOTS TO CREATE! This is the bug.');
        console.error('Debug info:', {
          weeks,
          selectedDays: eventForm.selectedDays,
          startTime: eventForm.startTime,
          endTime: eventForm.endTime,
          seriesId
        });
        setError('No slots were generated. Check your form inputs.');
        setLoading(false);
        return;
      }
      
      // Create each slot directly
      const createdSlots = [];
      for (let i = 0; i < slotsToCreate.length; i++) {
        const slotData = slotsToCreate[i];
        try {
          console.log(`Creating slot ${i + 1}/${slotsToCreate.length}:`, slotData);
          const response = await timeSlotService.createTimeSlot(slotData);
          if (response.success) {
            createdSlots.push(response.data.timeSlot);
            console.log(`✅ Created slot ${i + 1}: ${slotData.date} ${slotData.startTime}-${slotData.endTime}`);
          } else {
            console.error(`❌ Failed to create slot ${i + 1}:`, response.error);
            console.error('Full response:', response);
          }
        } catch (error) {
          console.error(`❌ Error creating slot ${i + 1}:`, error);
        }
      }
      
      console.log(`✅ RECURRING CREATION COMPLETE: ${createdSlots.length}/${slotsToCreate.length} slots created`);
      
      // Remove the temporary event and reload to show real slots
      setEvents(prev => prev.filter(event => event.id !== selectedEvent.id));
      setShowEventDialog(false);
      setSelectedEvent(null);
      
      setSuccess(`Created ${createdSlots.length} recurring slots for ${eventForm.selectedDays.join(', ')} over ${weeks} weeks`);
      
      // Reload to show the new slots
      await loadWeeklyAvailability();
      
    } catch (error) {
      console.error('Error creating recurring slots:', error);
      setError('Failed to create recurring slots');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting an event
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    // Save current state for undo
    setLastActionEvents([...events]);

    try {
      setLoading(true);
      console.log('🗑️ Deleting time slot:', selectedEvent);
      
      // If this is an individual TimeSlot (has timeSlotId), delete from database
      if (selectedEvent.resource?.timeSlotId) {
        console.log('🗑️ Deleting individual TimeSlot from database:', selectedEvent.resource.timeSlotId);
        await timeSlotService.deleteTimeSlot(selectedEvent.resource.timeSlotId);
        console.log('✅ TimeSlot deleted from database');
      } else {
        console.log('🗑️ Deleting template event from UI only');
      }
      
      // Remove from UI
      setEvents(prev => prev.filter(event => event.id !== selectedEvent.id));
      setShowEventDialog(false);
      setSelectedEvent(null);
      setSuccess('Time slot deleted successfully');
      
    } catch (error) {
      console.error('Error deleting time slot:', error);
      setError('Failed to delete time slot');
    } finally {
      setLoading(false);
    }
  };

  // Handle recurring event option selection
  const handleRecurringOptionSelect = (action) => {
    setRecurringAction(action);
    setShowRecurringOptionsDialog(false);
    
    if (action === 'remaining') {
      // Cancel remaining sessions - immediate action
      handleCancelRemainingEvents();
    } else {
      // For 'single' or 'series', show the edit dialog
      const isEditingSeries = action === 'series';
      const recurringInfo = isEditingSeries ? extractRecurringInfoFromEvent(selectedEvent) : null;
      
      console.log('🔍 EDIT DIALOG INITIALIZATION:', {
        action,
        isEditingSeries,
        selectedEvent: selectedEvent?.resource,
        recurringInfo,
        extractedType: recurringInfo?.type,
        extractedWeeks: recurringInfo?.totalWeeks,
        extractedPattern: recurringInfo?.pattern,
        originalNotes: selectedEvent?.resource?.notes
      });
      
      const finalRecurringType = isEditingSeries && recurringInfo ? (recurringInfo.type || 'occurrences') : 'occurrences';
      
      console.log('🔍 FINAL RECURRING TYPE LOGIC:', {
        isEditingSeries,
        recurringInfoExists: !!recurringInfo,
        recurringInfoType: recurringInfo?.type,
        fallbackUsed: isEditingSeries && recurringInfo && !recurringInfo.type,
        finalRecurringType
      });
      
      console.log('🔍 FORM INITIALIZATION VALUES:', {
        isEditingSeries,
        recurringInfoExists: !!recurringInfo,
        recurringInfoType: recurringInfo?.type,
        recurringInfoEndDate: recurringInfo?.endDate,
        finalRecurringType,
        shouldShowNumberOfWeeks: finalRecurringType === 'occurrences',
        finalEndDate: isEditingSeries && recurringInfo?.endDate ? recurringInfo.endDate : ''
      });

      setEventForm({
        title: '',
        startTime: selectedEvent.resource.startTime,
        endTime: selectedEvent.resource.endTime,
        rateOverride: selectedEvent.resource.rateOverride || '',
        isRecurring: isEditingSeries,
        // FIXED: Use actual series info when editing series
        recurringType: finalRecurringType,
        recurringEndDate: isEditingSeries && recurringInfo?.endDate ? recurringInfo.endDate : '',
        recurringOccurrences: isEditingSeries && recurringInfo?.totalWeeks ? recurringInfo.totalWeeks : 4,
        selectedDays: isEditingSeries && recurringInfo?.pattern ? recurringInfo.pattern.split(',') : [selectedEvent.resource.day]
      });
      setIsEditing(false);
      setShowEventDialog(true);
    }
  };

  // Delete entire recurring series
  const handleDeleteRecurringSeries = async (seriesId) => {
    if (!seriesId) return;
    
    try {
      setLoading(true);
      console.log('🗑️ Deleting entire recurring series:', seriesId);
      
      // Get all slots in the series
      const response = await timeSlotService.getMyTimeSlots({
        startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
        endDate: moment().add(12, 'weeks').format('YYYY-MM-DD')
      });
      
      if (response.success && response.data?.timeSlots) {
        const seriesSlots = response.data.timeSlots.filter(slot => 
          slot.recurringSeriesId === seriesId || 
          (slot.notes && (slot.notes.includes(`SeriesId:${seriesId}`) || 
                         slot.notes.includes(`Recurring Series: ${seriesId}`)))
        );
        
        console.log(`🗑️ Found ${seriesSlots.length} slots in series to delete`);
        
        for (const slot of seriesSlots) {
          try {
            await timeSlotService.deleteTimeSlot(slot._id);
            console.log(`✅ Deleted series slot: ${slot.date} ${slot.startTime}-${slot.endTime}`);
          } catch (error) {
            console.error(`❌ Failed to delete series slot ${slot._id}:`, error);
          }
        }
        
        setSuccess(`Deleted recurring series (${seriesSlots.length} slots)`);
        await loadWeeklyAvailability();
      }
    } catch (error) {
      console.error('Error deleting recurring series:', error);
      setError('Failed to delete recurring series');
    } finally {
      setLoading(false);
    }
  };

  // Cancel remaining events in the series
  const handleCancelRemainingEvents = () => {
    if (!selectedEvent || !selectedEvent.resource.recurringGroup) return;

    const recurringGroupId = selectedEvent.resource.recurringGroup;
    const selectedEventDate = moment(selectedEvent.start);
    
    // Remove all events in the series that occur on or after the selected event's date
    setEvents(prev => prev.filter(event => {
      if (event.resource.recurringGroup === recurringGroupId) {
        const eventDate = moment(event.start);
        return eventDate.isBefore(selectedEventDate, 'day');
      }
      return true;
    }));

    setSelectedEvent(null);
    setSuccess('All remaining sessions in the series have been cancelled');
  };

  // Handle single event edit (only affects one instance)
  const handleSingleEventEdit = () => {
    if (!selectedEvent) return;

    const updatedEvent = {
      ...selectedEvent,
      title: `${eventForm.startTime} - ${eventForm.endTime}`, // Just show time
      resource: {
        ...selectedEvent.resource,
        startTime: eventForm.startTime,
        endTime: eventForm.endTime,
        rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null,
        // Remove recurring properties to make it a single event
        isRecurring: false,
        recurringGroup: undefined
      }
    };

    setEvents(prev => prev.map(event => 
      event.id === selectedEvent.id ? updatedEvent : event
    ));

    setShowEventDialog(false);
    setSelectedEvent(null);
    setRecurringAction(null);
    setSuccess('Single event updated successfully');
  };

  // Handle series edit (affects all events in the series)
  const handleSeriesEdit = async () => {
    if (!selectedEvent || !selectedEvent.resource.recurringGroup) return;

    const recurringGroupId = selectedEvent.resource.recurringGroup;
    
    try {
      setLoading(true);
      console.log('🔄 Updating entire recurring series:', recurringGroupId);
      
      // Find all events in the series
      const seriesEvents = events.filter(event => 
        event.resource.recurringGroup === recurringGroupId
      );
      
      console.log(`🔄 Found ${seriesEvents.length} existing series events`);
      
      // Get current series info
      const currentTotalWeeks = selectedEvent.resource.recurringTotalWeeks || seriesEvents.length;
      const newTotalWeeks = eventForm.recurringOccurrences;
      const selectedDays = eventForm.selectedDays;
      
      console.log('📊 Series comparison:', {
        currentTotalWeeks,
        newTotalWeeks,
        selectedDays,
        recurringGroupId
      });
      
      // Update existing slots (time and rate changes)
      const eventsToUpdate = seriesEvents.filter(event => event.resource.timeSlotId);
      console.log(`🔄 Updating ${eventsToUpdate.length} existing slots with new time/rate`);
      
      for (const event of eventsToUpdate) {
        try {
          const updateData = {
            startTime: eventForm.startTime,
            endTime: eventForm.endTime,
            rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null
          };
          
          await timeSlotService.updateTimeSlot(event.resource.timeSlotId, updateData);
          console.log(`✅ Updated series slot: ${event.resource.timeSlotId}`);
        } catch (error) {
          console.error(`❌ Failed to update series slot ${event.resource.timeSlotId}:`, error);
        }
      }
      
      // Handle week count changes
      if (newTotalWeeks > currentTotalWeeks) {
        console.log(`➕ Adding ${newTotalWeeks - currentTotalWeeks} additional weeks to series`);
        
        // Find the first event to get the pattern start date
        const firstEvent = seriesEvents.sort((a, b) => new Date(a.start) - new Date(b.start))[0];
        const seriesStartDate = moment(firstEvent.start);
        
        // Create additional slots for the new weeks
        const newSlots = [];
        
        for (let week = currentTotalWeeks; week < newTotalWeeks; week++) {
          for (const day of selectedDays) {
            const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase());
            const slotDate = seriesStartDate.clone().add(week, 'weeks').day(dayIndex);
            
            // Skip if in the past
            if (slotDate.isBefore(moment(), 'day')) {
              console.log(`⏭️ Skipping past date: ${slotDate.format('YYYY-MM-DD')}`);
              continue;
            }
            
            let notesData = `RECURRING|SeriesId:${recurringGroupId}|Week:${week + 1}|TotalWeeks:${newTotalWeeks}|Days:${selectedDays.join(',')}|Type:${eventForm.recurringType}`;
            
            // Add end date to notes if using "Until end date" option
            if (eventForm.recurringType === 'weeks' && eventForm.recurringEndDate) {
              notesData += `|EndDate:${eventForm.recurringEndDate}`;
            }
            
            const slotToCreate = {
              date: slotDate.format('YYYY-MM-DD'),
              startTime: eventForm.startTime,
              endTime: eventForm.endTime,
              notes: notesData,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            };
            
            if (eventForm.rateOverride && parseFloat(eventForm.rateOverride) > 0) {
              slotToCreate.rateOverride = parseFloat(eventForm.rateOverride);
            }
            
            try {
              const result = await timeSlotService.createTimeSlot(slotToCreate);
              console.log(`✅ Created additional slot for week ${week + 1}:`, slotDate.format('YYYY-MM-DD'));
              
              // Add to UI immediately
              if (result.success && result.data) {
                const newEvent = {
                  id: `individual-${result.data._id}`,
                  title: `🔁 Week ${week + 1} of ${newTotalWeeks}`,
                  start: slotDate.clone().hour(parseInt(eventForm.startTime.split(':')[0])).minute(parseInt(eventForm.startTime.split(':')[1])).toDate(),
                  end: slotDate.clone().hour(parseInt(eventForm.endTime.split(':')[0])).minute(parseInt(eventForm.endTime.split(':')[1])).toDate(),
                  resource: {
                    day: day.toLowerCase(),
                    startTime: eventForm.startTime,
                    endTime: eventForm.endTime,
                    rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null,
                    timeSlotId: result.data._id,
                    isIndividual: true,
                    isTemplate: false,
                    status: 'available',
                    isRecurring: true,
                    recurringGroup: recurringGroupId,
                    recurringSeriesId: recurringGroupId,
                    recurringWeekNumber: week + 1,
                    recurringTotalWeeks: newTotalWeeks
                  }
                };
                
                setEvents(prev => [...prev, newEvent]);
              }
            } catch (error) {
              console.error(`❌ Failed to create additional slot for week ${week + 1}:`, error);
            }
          }
        }
      } else if (newTotalWeeks < currentTotalWeeks) {
        console.log(`➖ Removing ${currentTotalWeeks - newTotalWeeks} weeks from series`);
        
        // Delete slots for removed weeks
        const slotsToDelete = seriesEvents.filter(event => {
          const weekNumber = event.resource.recurringWeekNumber || extractWeekNumberFromNotes(event.resource.notes);
          return weekNumber > newTotalWeeks;
        });
        
        console.log(`🗑️ Deleting ${slotsToDelete.length} slots for removed weeks`);
        
        for (const slot of slotsToDelete) {
          if (slot.resource.timeSlotId) {
            try {
              await timeSlotService.deleteTimeSlot(slot.resource.timeSlotId);
              console.log(`✅ Deleted slot for removed week: ${slot.resource.timeSlotId}`);
            } catch (error) {
              console.error(`❌ Failed to delete slot ${slot.resource.timeSlotId}:`, error);
            }
          }
        }
        
        // Remove from UI
        setEvents(prev => prev.filter(event => 
          event.resource.recurringGroup !== recurringGroupId || 
          (event.resource.recurringWeekNumber || extractWeekNumberFromNotes(event.resource.notes)) <= newTotalWeeks
        ));
      }
      
      // Update all remaining events in the series in UI state
      setEvents(prev => prev.map(event => {
        if (event.resource.recurringGroup === recurringGroupId) {
          const weekNumber = event.resource.recurringWeekNumber || extractWeekNumberFromNotes(event.resource.notes);
          return {
            ...event,
            title: `🔁 Week ${weekNumber} of ${newTotalWeeks}`,
            resource: {
              ...event.resource,
              startTime: eventForm.startTime,
              endTime: eventForm.endTime,
              rateOverride: eventForm.rateOverride ? parseFloat(eventForm.rateOverride) : null,
              recurringTotalWeeks: newTotalWeeks
              // KEEP all other recurring properties intact
            }
          };
        }
        return event;
      }));

      setSuccess(`Series updated: ${newTotalWeeks} weeks total`);
    } catch (error) {
      console.error('Error updating recurring series:', error);
      setError('Failed to update recurring series');
    } finally {
      setLoading(false);
      setShowEventDialog(false);
      setSelectedEvent(null);
      setRecurringAction(null);
    }
  };

  // Convert events back to weekly template format
  const convertEventsToTemplate = () => {
    console.log('🔄 === CONVERT EVENTS TO TEMPLATE DEBUG ===');
    console.log('🔍 Total events to process:', events.length);
    console.log('🔍 All events:', events.map(e => ({
      id: e.id,
      title: e.title,
      resource: e.resource
    })));
    
    // Get new template events (both recurring and regular template-worthy events)
    const newTemplateEvents = events.filter(event => {
      const hasValidResource = event.resource && 
                              event.resource.day && 
                              event.resource.startTime && 
                              event.resource.endTime;
      
      if (!hasValidResource) {
        console.warn('Skipping invalid event:', event);
        return false;
      }
      
      // Include template events (created from recurring)
      if (event.resource.isTemplate) {
        console.log('Including recurring template event:', event.id);
        return true;
      }
      
      // Include new events (created by click and drag) only if they should be part of template
      // Skip isNew events that are non-recurring - they should be saved as individual slots
      if (event.resource.isNew) {
        console.log('Found isNew event, checking if it should be included in template:', event.id);
        console.log('Event resource:', event.resource);
        
        // Check if this was created through the recurring dialog flow
        // If it has been processed through handleSaveEvent and is meant to be recurring, 
        // it would have been converted to isTemplate already
        // So all remaining isNew events should be individual slots
        console.log('Skipping isNew event - will save as individual slot:', event.id);
        return false;
      }
      
      // Include regular non-individual events (created by click and drag)
      if (!event.resource.isIndividual && !event.resource.isRecurring && !event.resource.isTemplate) {
        console.log('Including regular template event:', event.id);
        return true;
      }
      
      // Skip individual time slots and recurring instances
      if (event.resource.isIndividual) {
        console.log('Skipping individual time slot:', event.id);
        return false;
      }
      
      // Skip recurring event instances
      if (event.resource.isRecurring && event.resource.recurringGroup) {
        console.log('Skipping recurring instance:', event.id);
        return false;
      }
      
      return false;
    });

    console.log(`🔍 Found ${newTemplateEvents.length} new template events out of ${events.length} total events`);
    console.log('📋 Template events details:', newTemplateEvents.map(e => ({ 
      id: e.id, 
      day: e.resource.day, 
      time: e.resource.startTime + '-' + e.resource.endTime,
      isTemplate: e.resource.isTemplate,
      isNew: e.resource.isNew,
      isIndividual: e.resource.isIndividual
    })));
    
    // Check for duplicate template events
    const dayTimeMap = {};
    newTemplateEvents.forEach(event => {
      const key = `${event.resource.day}-${event.resource.startTime}-${event.resource.endTime}`;
      dayTimeMap[key] = (dayTimeMap[key] || 0) + 1;
    });
    
    const duplicates = Object.entries(dayTimeMap).filter(([key, count]) => count > 1);
    if (duplicates.length > 0) {
      console.warn('⚠️ FOUND DUPLICATE TEMPLATE EVENTS:', duplicates);
    } else {
      console.log('✅ No duplicate template events found');
    }

    // Convert new template events to schedule format
    const newScheduleSlots = newTemplateEvents.map(event => ({
      day: event.resource.day.toLowerCase(),
      startTime: event.resource.startTime,
      endTime: event.resource.endTime,
      rateOverride: event.resource.rateOverride || null
    }));

    // Determine if we should replace existing schedule or merge
    let finalSchedule = [];
    
    if (newScheduleSlots.length > 0) {
      console.log(`Creating new template with ${newScheduleSlots.length} slots (replacing any existing)`);
      // If we have new template events, use them (replace existing)
      finalSchedule = newScheduleSlots;
    } else {
      // If no new template events, preserve existing schedule
      if (weeklyTemplate && weeklyTemplate.schedule) {
        finalSchedule = [...weeklyTemplate.schedule];
        console.log(`No new template events, preserving ${finalSchedule.length} existing template slots`);
      }
    }

    // Remove duplicates just in case
    const uniqueSchedule = finalSchedule.filter((slot, index, self) => {
      return index === self.findIndex(s => 
        s.day === slot.day && 
        s.startTime === slot.startTime && 
        s.endTime === slot.endTime
      );
    });

    console.log(`Final schedule: ${newScheduleSlots.length} new template events, ${uniqueSchedule.length} slots in final template`);

    // Sort by day and time
    const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    uniqueSchedule.sort((a, b) => {
      const dayDiff = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      if (dayDiff !== 0) return dayDiff;
      return a.startTime.localeCompare(b.startTime);
    });

    // Clean template structure - remove client-side properties that backend doesn't expect
    const template = {
      name: weeklyTemplate.name || 'My Weekly Schedule',
      description: weeklyTemplate.description || '',
      timezone: weeklyTemplate.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      isActive: true,
      schedule: uniqueSchedule
    };

    console.log('Final template structure (cleaned):', template);
    return template;
  };

  // Save the weekly schedule
  const handleSaveSchedule = async () => {
    // Prevent multiple concurrent saves
    if (loading) {
      console.warn('⚠️ Save already in progress, ignoring duplicate save request');
      return;
    }
    
    console.log('🚀 === STARTING SAVE SCHEDULE OPERATION ===');
    console.log('Current events array:', events);
    console.log('Current weeklyTemplate:', weeklyTemplate);
    
    try {
      setLoading(true);
      setError(null);

      console.log('⚙️ Step 1: Converting events to template...');
      console.log('🔍 Current events before conversion:', events.map(e => ({
        id: e.id,
        title: e.title,
        isNew: e.resource?.isNew,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual,
        isRecurring: e.resource?.isRecurring,
        day: e.resource?.day,
        startTime: e.resource?.startTime,
        endTime: e.resource?.endTime
      })));
      
      const template = convertEventsToTemplate();
      console.log('✅ Template conversion result:', template);
      
      // Get individual events first
      console.log('⚙️ Step 2: Filtering individual events...');
      const individualEvents = events.filter(event => event.resource?.isNew);
      console.log('✅ Individual events found:', individualEvents.length);
      console.log('📋 Individual events details:', individualEvents.map(e => ({
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end,
        day: e.resource?.day,
        startTime: e.resource?.startTime,
        endTime: e.resource?.endTime,
        isNew: e.resource?.isNew,
        isIndividual: e.resource?.isIndividual,
        isTemplate: e.resource?.isTemplate
      })));
      
      console.log('=== FINAL TEMPLATE VALIDATION ===');
      console.log('Template structure:', {
        name: template.name,
        scheduleLength: template.schedule?.length || 0,
        schedule: template.schedule,
        individualEventsCount: individualEvents.length
      });
      
      console.log('🔍 Decision tree for save logic:');
      console.log(`- Template has schedule: ${template.schedule && template.schedule.length > 0}`);
      console.log(`- Individual events exist: ${individualEvents.length > 0}`);
      console.log(`- Total events: ${events.length}`);
      console.log(`- Events breakdown:`, events.map(e => ({
        id: e.id,
        isNew: e.resource?.isNew,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual
      })));
      
      // Validate template before sending
      if (!template.schedule || template.schedule.length === 0) {
        // If we have individual events to save, process them even if template is empty
        if (individualEvents.length > 0) {
          console.log('Template is empty but we have individual events to save - continuing...');
          // Continue with individual slot processing below
        }
        // If no template events found, it might be because we have individual slots loaded
        // In this case, just save the existing weekly template structure  
        else if (events.length > 0 && events.every(event => event.resource?.isIndividual)) {
          console.log('Only existing individual slots found, saving existing weekly template structure');
          const existingTemplate = {
            name: weeklyTemplate.name || 'My Weekly Schedule',
            description: weeklyTemplate.description || '',
            timezone: weeklyTemplate.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            isActive: true,
            schedule: weeklyTemplate.schedule || []
          };
          
          if (existingTemplate.schedule.length === 0) {
            setError('No template or time slots found to save. Please create some availability first.');
            setLoading(false);
            return;
          }
          
          console.log('Saving existing template structure:', existingTemplate);
          const templateResponse = await weeklyAvailabilityService.saveWeeklyTemplate(existingTemplate);
          
          if (templateResponse.success) {
            setSuccess('Weekly schedule saved successfully!');
            setHasUnsavedChanges(false);
            loadWeeklyAvailability();
          } else {
            setError('Failed to save weekly schedule');
          }
          setLoading(false);
          return;
        } else if (individualEvents.length === 0) {
          setError('No valid time slots found to save. Please create some availability slots first.');
          setLoading(false);
          return;
        }
        // If we have individual events but no template, continue to process individual events
      }
      
      // Log the clean JSON object as requested
      console.log('=== SAVING NON-RECURRING SLOT ===');
      console.log('Current events:', events.map(e => ({
        id: e.id,
        title: e.title,
        isTemplate: e.resource?.isTemplate,
        isIndividual: e.resource?.isIndividual,
        isRecurring: e.resource?.isRecurring,
        day: e.resource?.day,
        startTime: e.resource?.startTime,
        endTime: e.resource?.endTime
      })));
      console.log('Weekly Schedule Template:', JSON.stringify(template, null, 2));
      console.log('Template validation:', {
        hasName: !!template.name,
        hasSchedule: !!template.schedule,
        scheduleLength: template.schedule.length,
        firstSlot: template.schedule[0],
        eventsCount: events.length,
        templateEventsCount: events.filter(e => e.resource?.isTemplate).length,
        regularEventsCount: events.filter(e => !e.resource?.isIndividual && !e.resource?.isRecurring && !e.resource?.isTemplate).length,
        newEventsCount: events.filter(e => e.resource?.isNew).length
      });

      // Final validation before saving
      if (!template.name || !template.schedule || !Array.isArray(template.schedule)) {
        console.error('Template validation failed:', {
          hasName: !!template.name,
          hasSchedule: !!template.schedule,
          isArray: Array.isArray(template.schedule),
          template: template
        });
        setError('Invalid template structure. Please try refreshing and creating slots again.');
        setLoading(false);
        return;
      }
      
      // Check if template is empty
      if (template.schedule.length === 0) {
        console.warn('Template has empty schedule, checking if we should save existing template...');
        // If we have no new template events but existing template exists, use existing template
        if (weeklyTemplate && weeklyTemplate.schedule && weeklyTemplate.schedule.length > 0) {
          console.log('Using existing weekly template since no new template events found');
          template.schedule = weeklyTemplate.schedule;
        } else {
          console.log('No template events and no existing template - checking individual events');
          if (individualEvents.length === 0) {
            setError('No availability slots found to save. Please create some time slots first.');
            setLoading(false);
            return;
          } else {
            console.log('Only individual events found, will skip template save');
            setSuccess(`Saved ${individualEvents.length} individual time slots successfully!`);
            
            // Verify the slots were saved by immediately checking the database
            console.log('🔍 Verifying saved slots by reloading...');
            setTimeout(async () => {
              try {
                const verifyResponse = await timeSlotService.getMyTimeSlots({
                  startDate: moment().startOf('week').format('YYYY-MM-DD'),
                  endDate: moment().startOf('week').add(2, 'weeks').format('YYYY-MM-DD')
                });
                console.log('🔍 Verification: TimeSlots after save:', verifyResponse.data?.timeSlots);
              } catch (verifyError) {
                console.error('🔍 Verification failed:', verifyError);
              }
              
              loadWeeklyAvailability();
            }, 1000); // Wait 1 second for database to sync
            
            setLoading(false);
            return;
          }
        }
      }

      // Save template to database
      console.log('🚀 === ABOUT TO SEND TEMPLATE TO API ===');
      console.log('📊 Template schedule analysis:');
      console.log(`- Total schedule slots: ${template.schedule.length}`);
      console.log('- Schedule breakdown by day:');
      
      const dayCount = {};
      template.schedule.forEach((slot, i) => {
        dayCount[slot.day] = (dayCount[slot.day] || 0) + 1;
        console.log(`  Slot ${i + 1}: ${slot.day} ${slot.startTime}-${slot.endTime}`);
      });
      
      console.log('- Day frequency:', dayCount);
      console.log('- Unique days:', Object.keys(dayCount).length);
      console.log('- Total slots per day:', Object.values(dayCount));
      
      console.log('📤 Full template being sent:', JSON.stringify(template, null, 2));
      
      // NEW LOGIC: Skip template processing for recurring events since they're created directly
      const hasRecurringInfo = template.recurringInfo && template.recurringInfo.selectedDays && template.recurringInfo.selectedDays.length > 0;
      if (hasRecurringInfo) {
        console.log('⚠️ BYPASSING TEMPLATE LOGIC: Recurring events are now created directly via handleCreateRecurringSlots()');
        setError('❌ Old template workflow detected! Recurring events are now created instantly. Please create recurring events using the "Make Recurring" option in the event dialog.');
        setLoading(false);
        return;
      }
      
      // First, save any individual non-recurring slots (isNew events)
      console.log(`⚙️ Step 3: Processing ${individualEvents.length} individual events to save as TimeSlots...`);
      
      // Check for existing TimeSlots in the date range to avoid conflicts
      if (individualEvents.length > 0) {
        const startDate = moment().startOf('week').format('YYYY-MM-DD');
        const endDate = moment().startOf('week').add(2, 'weeks').format('YYYY-MM-DD');
        
        console.log('🔍 Checking existing TimeSlots to avoid conflicts...');
        try {
          const existingResponse = await timeSlotService.getMyTimeSlots({
            startDate,
            endDate
          });
          
          if (existingResponse.success && existingResponse.data?.timeSlots) {
            console.log(`📋 Found ${existingResponse.data.timeSlots.length} existing TimeSlots:`, 
              existingResponse.data.timeSlots.map(slot => ({
                date: slot.date,
                startTime: slot.startTime,
                endTime: slot.endTime,
                status: slot.status
              }))
            );
          }
        } catch (checkError) {
          console.warn('⚠️ Could not check existing TimeSlots:', checkError.message);
        }
      }
      
      for (let i = 0; i < individualEvents.length; i++) {
        const event = individualEvents[i];
        console.log(`⚙️ Step 3.${i + 1}: Processing individual event:`, event);
        
        // Prepare slot data outside try block so it's accessible in catch
        const slotDate = moment(event.start).format('YYYY-MM-DD');
        console.log(`📅 Individual slot ${i + 1} date calculation:`, {
          eventStart: event.start,
          eventStartFormatted: moment(event.start).format('YYYY-MM-DD HH:mm'),
          slotDate: slotDate,
          dayOfWeek: moment(event.start).format('dddd'),
          isInCurrentWeek: moment(event.start).isSame(moment(), 'week'),
          isInLoadRange: moment(event.start).isBetween(
            moment().startOf('week').subtract(1, 'week'), 
            moment().startOf('week').add(8, 'weeks'), 
            'day', 
            '[]'
          )
        });
        
        const timeSlotData = {
          date: slotDate,
          startTime: event.resource.startTime,
          endTime: event.resource.endTime,
          rateOverride: event.resource.rateOverride || null,
          notes: '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        try {
          console.log(`📤 Creating individual TimeSlot ${i + 1}:`, timeSlotData);
          console.log(`📍 Slot date analysis:`, {
            slotDate,
            willBeVisibleAfterReload: moment(slotDate).isBetween(
              moment().startOf('week').subtract(1, 'week'),
              moment().startOf('week').add(8, 'weeks'),
              'day',
              '[]'
            ),
            loadRangeStart: moment().startOf('week').subtract(1, 'week').format('YYYY-MM-DD'),
            loadRangeEnd: moment().startOf('week').add(8, 'weeks').format('YYYY-MM-DD')
          });
          const slotResponse = await timeSlotService.createTimeSlot(timeSlotData);
          console.log(`✅ Individual TimeSlot ${i + 1} created successfully:`, slotResponse);
        } catch (slotError) {
          console.error(`❌ Error creating individual TimeSlot ${i + 1}:`, slotError);
          console.error('Error details:', {
            message: slotError.message,
            response: slotError.response?.data,
            status: slotError.response?.status
          });
          
          // Handle 409 conflict errors specifically
          if (slotError.response?.status === 409) {
            const conflictingSlot = slotError.response.data?.error?.conflictingSlot;
            console.warn(`⚠️ TimeSlot ${i + 1} conflicts with existing slot:`, conflictingSlot);
            
            // Check if the conflicting slot is exactly the same (might be a duplicate)
            if (conflictingSlot && 
                conflictingSlot.startTime === timeSlotData.startTime && 
                conflictingSlot.endTime === timeSlotData.endTime) {
              console.log(`✅ Slot ${i + 1} already exists with same times - skipping creation`);
              continue; // Skip this slot but continue with others
            } else {
              setError(`Time slot ${i + 1} (${timeSlotData.startTime}-${timeSlotData.endTime}) conflicts with existing slot (${conflictingSlot?.startTime}-${conflictingSlot?.endTime})`);
              setLoading(false);
              return;
            }
          } else {
            setError(`Failed to save individual time slot ${i + 1}: ${slotError.message}`);
            setLoading(false);
            return;
          }
        }
      }
      
      console.log('✅ All individual slots processed successfully');
      
      // Now save the template (without the individual events)
      console.log('⚙️ Step 4: Saving template after individual slots processed...');
      console.log('📤 Template being sent to API:', JSON.stringify(template, null, 2));
      
      // Final validation check before sending
      if (!template || typeof template !== 'object') {
        console.error('❌ Invalid template object:', template);
        setError('Invalid template structure generated');
        setLoading(false);
        return;
      }
      
      if (!template.schedule || !Array.isArray(template.schedule)) {
        console.error('❌ Invalid template schedule:', template.schedule);
        setError('Invalid template schedule structure');
        setLoading(false);
        return;
      }
      
      // Check if template schedule is empty - if so, and we have individual events, skip template save
      if (template.schedule.length === 0 && individualEvents.length > 0) {
        console.log('✅ Template schedule is empty but individual events were saved. Skipping template save.');
        setSuccess(`Successfully saved ${individualEvents.length} individual time slots!`);
        setHasUnsavedChanges(false);
        
        // Add delay before reload to ensure database commits individual slots
        console.log('⏳ Waiting for individual slots to be committed to database...');
        setTimeout(async () => {
          console.log('🔄 Reloading after individual slot save...');
          await loadWeeklyAvailability();
          setLoading(false);
        }, 1500); // 1.5 second delay for database commit
        return;
      }
      
      console.log('✅ Template validation passed, sending to API...');
      
      // TEMPORARY DEBUG: Check if template save is causing the 500 error
      console.log('🔍 TEMP DEBUG: About to save template. Checking template structure...');
      console.log('🔍 Template schedule content:', template.schedule);
      console.log('🔍 Each schedule slot:', template.schedule.map((slot, i) => ({
        index: i,
        day: slot.day,
        startTime: slot.startTime,
        endTime: slot.endTime,
        type: typeof slot.day,
        keys: Object.keys(slot)
      })));
      
      // Check if we should skip template save for debugging
      if (template.schedule.length === 0) {
        console.log('⚠️ TEMP DEBUG: Skipping empty template save to avoid 500 error');
        if (individualEvents.length > 0) {
          setSuccess(`Successfully saved ${individualEvents.length} individual time slots! (Skipped empty template save)`);
        } else {
          setSuccess('No changes to save');
        }
        setHasUnsavedChanges(false);
        setLoading(false);
        loadWeeklyAvailability();
        return;
      }
      
      console.log('🚀 ABOUT TO SAVE TEMPLATE - Final Check:');
      console.log('- Template recurring info being saved:', template.recurringInfo);
      console.log('- Expected slots per week:', template.schedule.length);
      console.log('- Expected total weeks:', template.recurringInfo?.occurrences || 'none');
      console.log('- Expected total slots:', (template.schedule.length * (template.recurringInfo?.occurrences || 4)));
      
      const templateResponse = await weeklyAvailabilityService.saveWeeklyTemplate(template);
      console.log('✅ Template save response:', templateResponse);
      
      // Check if the backend returned the same template we sent
      if (templateResponse.success && templateResponse.data?.weeklyTemplate?.recurringInfo) {
        console.log('🔍 BACKEND RETURNED recurringInfo:', templateResponse.data.weeklyTemplate.recurringInfo);
        const backendOccurrences = templateResponse.data.weeklyTemplate.recurringInfo.occurrences;
        const frontendOccurrences = template.recurringInfo.occurrences;
        console.log(`🔍 COMPARISON: Frontend sent ${frontendOccurrences}, Backend returned ${backendOccurrences}`);
        if (backendOccurrences !== frontendOccurrences) {
          console.warn('⚠️ MISMATCH: Backend returned different occurrences than frontend sent!');
        }
      }
      
      if (templateResponse.success) {
        setWeeklyTemplate(template);
        
        // REMOVED: The logic that prevented regeneration when slots already exist
        // This was causing issues when editing recurring series - we WANT to regenerate with new parameters
        console.log('🔄 Proceeding with slot generation (allowing regeneration for template updates)');
        
        // Generate actual time slots from template
        // Use recurring info if available, otherwise default to 4 weeks
        let startDate, endDate;
        
        if (template.recurringInfo) {
          // Start from current week for recurring slots
          startDate = moment().startOf('week').format('YYYY-MM-DD');
          
          console.log('🔍 Recurring info details:', {
            type: template.recurringInfo.type,
            endDate: template.recurringInfo.endDate,
            occurrences: template.recurringInfo.occurrences,
            selectedDays: template.recurringInfo.selectedDays
          });
          
          if (template.recurringInfo.type === 'weeks' && template.recurringInfo.endDate) {
            endDate = moment(template.recurringInfo.endDate).format('YYYY-MM-DD');
            console.log(`📅 Using explicit end date: ${endDate}`);
          } else {
            console.log('🔍 DEEP DEBUG - Parsing weeks from template:');
            console.log('- template.recurringInfo:', template.recurringInfo);
            console.log('- template.recurringInfo.occurrences (raw):', template.recurringInfo.occurrences);
            console.log('- typeof template.recurringInfo.occurrences:', typeof template.recurringInfo.occurrences);
            console.log('- parseInt(template.recurringInfo.occurrences):', parseInt(template.recurringInfo.occurrences));
            console.log('- parseInt(...) || 4:', parseInt(template.recurringInfo.occurrences) || 4);
            
            // More robust parsing - handle both string and number types
            const rawOccurrences = template.recurringInfo.occurrences;
            const weeks = (typeof rawOccurrences === 'number') 
              ? rawOccurrences 
              : parseInt(rawOccurrences) || 4;
            // FIXED: Proper date range calculation for recurring weeks
            // For N weeks, we need to end at the last day of the Nth week
            if (weeks === 1) {
              // Special case: 1 week means current week only (Monday to Sunday)
              endDate = moment().startOf('week').endOf('week').format('YYYY-MM-DD');
            } else {
              // For multiple weeks: start from current week and add (weeks-1) more full weeks
              const additionalWeeks = weeks - 1;
              endDate = moment().startOf('week').add(additionalWeeks, 'weeks').endOf('week').format('YYYY-MM-DD');
            }
            console.log(`📅 RECURRING WEEKS DEBUG:
              - User requested: ${weeks} weeks total
              - Template stored occurrences: ${template.recurringInfo.occurrences}
              - Parsed weeks: ${weeks}
              - Start date: ${startDate}
              - End date: ${endDate}
              - Current week start: ${moment().startOf('week').format('YYYY-MM-DD')}
              - Current week end: ${moment().startOf('week').endOf('week').format('YYYY-MM-DD')}
              - Date range spans: ${moment(endDate).diff(moment(startDate), 'days') + 1} days
              - Week count in range: ${Math.ceil((moment(endDate).diff(moment(startDate), 'days') + 1) / 7)}
              - Template schedule length: ${template.schedule.length} slots per week
              - Expected total slots: ${template.schedule.length} × ${weeks} = ${template.schedule.length * weeks}`);
          }
          
          // Clear template events from current state before generating
          setEvents(prev => prev.filter(event => !event.resource?.isTemplate));
        } else {
          // Default to 4 weeks total if no recurring info
          startDate = moment().startOf('week').format('YYYY-MM-DD');
          endDate = moment().startOf('week').add(3, 'weeks').endOf('week').format('YYYY-MM-DD'); // 4 weeks total = end of 4th week
        }
        
        console.log('📅 === TIME SLOT GENERATION ANALYSIS ===');
        console.log(`- Generation date range: ${startDate} to ${endDate}`);
        console.log(`- Template schedule length: ${template.schedule.length}`);
        console.log('- Template schedule:', template.schedule);
        console.log('- Template recurring info:', template.recurringInfo);
        
        // Calculate and log expected vs actual weeks
        const actualWeekCount = moment(endDate).diff(moment(startDate), 'weeks') + 1;
        const expectedWeekCount = template.recurringInfo?.occurrences || 4;
        console.log(`- Expected weeks from user input: ${expectedWeekCount}`);
        console.log(`- Actual weeks calculated: ${actualWeekCount}`);
        console.log(`- Weeks match: ${actualWeekCount === expectedWeekCount ? '✅ YES' : '❌ NO'}`);
        
        if (template.recurringInfo?.type === 'occurrences') {
          console.log(`- DEBUGGING: User selected ${expectedWeekCount} weeks, system will generate slots for ${actualWeekCount} weeks`);
        }
        
        // Calculate expected slots
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        const totalDays = endMoment.diff(startMoment, 'days') + 1;
        const totalWeeks = Math.ceil(totalDays / 7);
        const expectedSlots = template.schedule.length * totalWeeks;
        
        console.log(`📊 Date range calculation:`);
        console.log(`- Start: ${startDate} (${startMoment.format('dddd')})`);
        console.log(`- End: ${endDate} (${endMoment.format('dddd')})`);  
        console.log(`- Total days: ${totalDays}`);
        console.log(`- Total weeks: ${totalWeeks}`);
        console.log(`- Expected slots: ${template.schedule.length} × ${totalWeeks} = ${expectedSlots}`);
        
        
        // CRITICAL: Delete ALL existing template-generated slots to prevent duplicates
        // This is especially important when editing existing recurring series
        console.log('🗑️ === CLEANUP PHASE: Deleting existing template slots ===');
        console.log('🔍 CONTEXT: This might be editing an existing recurring series, so we clean ALL template AND recurring slots');
        
        // FIRST: Let's see what's already in the database
        console.log('🔍 PRE-CLEANUP: Checking what slots already exist...');
        const preCleanupResponse = await timeSlotService.getMyTimeSlots({
          startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
          endDate: moment().add(2, 'months').format('YYYY-MM-DD')
        });
        
        if (preCleanupResponse.success && preCleanupResponse.data?.timeSlots) {
          const existingSlots = preCleanupResponse.data.timeSlots;
          console.log(`🔍 PRE-CLEANUP: Found ${existingSlots.length} existing slots in database`);
          const slotsByWeek = {};
          existingSlots.forEach(slot => {
            const weekKey = moment(slot.date).startOf('week').format('YYYY-MM-DD');
            if (!slotsByWeek[weekKey]) slotsByWeek[weekKey] = [];
            slotsByWeek[weekKey].push(slot);
          });
          console.log('🔍 PRE-CLEANUP: Existing slots by week:', Object.keys(slotsByWeek).map(week => ({
            week,
            count: slotsByWeek[week].length,
            times: [...new Set(slotsByWeek[week].map(s => s.startTime + '-' + s.endTime))]
          })));
        }
        
        try {
          // Use wider range to ensure we catch all template-generated slots
          const cleanupStartDate = moment().startOf('year').format('YYYY-MM-DD');
          const cleanupEndDate = moment().endOf('year').format('YYYY-MM-DD');
          
          console.log(`🔍 Fetching all template slots in range: ${cleanupStartDate} to ${cleanupEndDate}`);
          const allExistingResponse = await timeSlotService.getMyTimeSlots({
            startDate: cleanupStartDate,
            endDate: cleanupEndDate
          });
          
          if (allExistingResponse.success && allExistingResponse.data?.timeSlots) {
            // ROOT CAUSE FIX: The issue is that we need to delete ALL slots that match the template times
            // regardless of their generation metadata, because old slots might have different/missing templateIds
            
            console.log(`🔍 BEFORE CLEANUP: Found ${allExistingResponse.data.timeSlots.length} total slots in database`);
            console.log('🔍 Template schedule to match:', template.schedule);
            
            const slotsToDelete = allExistingResponse.data.timeSlots.filter(slot => {
              // AGGRESSIVE CLEANUP: Delete ALL available slots that match template times
              // This is more reliable than trying to track templateIds across saves
              
              const matchesTemplatePattern = template.schedule.some(scheduleItem => 
                scheduleItem.startTime === slot.startTime && 
                scheduleItem.endTime === slot.endTime
              );
              
              const isAvailableSlot = slot.status === 'available';
              const isInFuture = moment(slot.date).isSameOrAfter(moment(), 'day');
              
              // Only delete if it matches template times AND is available AND is in future
              const shouldDelete = matchesTemplatePattern && isAvailableSlot && isInFuture;
              
              if (shouldDelete) {
                console.log(`🗑️ WILL DELETE: ${slot._id} - ${slot.date} ${slot.startTime}-${slot.endTime} (matches template pattern)`);
              }
              
              return shouldDelete;
            });
            
            console.log(`🗑️ AGGRESSIVE CLEANUP: Found ${slotsToDelete.length} slots to delete (all available slots matching template times):`, 
              slotsToDelete.map(s => ({ 
                id: s._id, 
                date: s.date, 
                time: s.startTime + '-' + s.endTime, 
                status: s.status,
                templateId: s.generatedFrom?.templateId || 'none'
              })));
            
            // Group by template ID to see which templates generated these slots
            const slotsByTemplate = {};
            slotsToDelete.forEach(slot => {
              const templateId = slot.generatedFrom?.templateId || 'unknown';
              if (!slotsByTemplate[templateId]) slotsByTemplate[templateId] = [];
              slotsByTemplate[templateId].push(slot);
            });
            console.log('🔍 Slots grouped by template ID:', Object.keys(slotsByTemplate).map(tid => ({
              templateId: tid,
              count: slotsByTemplate[tid].length,
              dateRange: `${slotsByTemplate[tid][0]?.date} to ${slotsByTemplate[tid][slotsByTemplate[tid].length - 1]?.date}`
            })));
            
            // Delete in batches to avoid overwhelming the server
            for (let i = 0; i < slotsToDelete.length; i++) {
              const slot = slotsToDelete[i];
              try {
                await timeSlotService.deleteTimeSlot(slot._id);
                console.log(`✅ ENHANCED DELETE: Deleted slot ${i + 1}/${slotsToDelete.length}: ${slot._id} (${slot.date} ${slot.startTime}-${slot.endTime})`);
                
                // Small delay between deletions to prevent server overload
                if (i < slotsToDelete.length - 1) {
                  await new Promise(resolve => setTimeout(resolve, 100));
                }
              } catch (deleteError) {
                console.error(`❌ Failed to delete slot ${slot._id}:`, deleteError);
              }
            }
            
            // Wait for deletions to be processed
            console.log('⏳ Waiting for cleanup to be processed...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // VERIFICATION: Double-check that cleanup worked
            console.log('🔍 VERIFYING CLEANUP: Checking if slots were actually deleted...');
            const verifyResponse = await timeSlotService.getMyTimeSlots({
              startDate: cleanupStartDate,
              endDate: cleanupEndDate
            });
            
            if (verifyResponse.success && verifyResponse.data?.timeSlots) {
              const remainingMatchingSlots = verifyResponse.data.timeSlots.filter(slot => {
                const matchesTemplate = template.schedule.some(scheduleItem => 
                  scheduleItem.startTime === slot.startTime && 
                  scheduleItem.endTime === slot.endTime
                );
                return matchesTemplate && slot.status === 'available' && moment(slot.date).isSameOrAfter(moment(), 'day');
              });
              
              console.log(`🔍 CLEANUP VERIFICATION: ${remainingMatchingSlots.length} matching slots remain after cleanup`);
              if (remainingMatchingSlots.length > 0) {
                console.warn('⚠️ Some slots were not deleted:', remainingMatchingSlots.map(s => ({
                  id: s._id,
                  date: s.date,
                  time: s.startTime + '-' + s.endTime
                })));
              } else {
                console.log('✅ CLEANUP SUCCESS: All matching slots were deleted');
              }
            }
            
            // POST-CLEANUP: Verify cleanup worked
            console.log('🔍 POST-CLEANUP: Verifying cleanup worked...');
            const postCleanupResponse = await timeSlotService.getMyTimeSlots({
              startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
              endDate: moment().add(2, 'months').format('YYYY-MM-DD')
            });
            
            if (postCleanupResponse.success && postCleanupResponse.data?.timeSlots) {
              const remainingSlots = postCleanupResponse.data.timeSlots;
              console.log(`🔍 POST-CLEANUP: ${remainingSlots.length} slots remain in database`);
              if (remainingSlots.length > 0) {
                const slotsByWeek = {};
                remainingSlots.forEach(slot => {
                  const weekKey = moment(slot.date).startOf('week').format('YYYY-MM-DD');
                  if (!slotsByWeek[weekKey]) slotsByWeek[weekKey] = [];
                  slotsByWeek[weekKey].push(slot);
                });
                console.log('🔍 POST-CLEANUP: Remaining slots by week:', Object.keys(slotsByWeek).map(week => ({
                  week,
                  count: slotsByWeek[week].length,
                  times: [...new Set(slotsByWeek[week].map(s => s.startTime + '-' + s.endTime))]
                })));
              } else {
                console.log('✅ POST-CLEANUP: Database is clean - no slots remain');
              }
            }
          }
        } catch (cleanupError) {
          console.error('⚠️ Failed to cleanup existing template slots:', cleanupError);
        }
        console.log('🗑️ === CLEANUP PHASE COMPLETE ===');
        
        console.log('🔧 === GENERATION PHASE: Creating new template slots ===');
        console.log(`📅 Generation parameters:`, {
          startDate,
          endDate,
          templateScheduleLength: template.schedule.length,
          templateSchedule: template.schedule
        });
        
        console.log('🚀 === CRITICAL: WHAT WE\'RE SENDING TO BACKEND ===');
        console.log('- Start Date:', startDate);
        console.log('- End Date:', endDate);
        console.log('- Date range spans days:', moment(endDate).diff(moment(startDate), 'days') + 1);
        console.log('- Template schedule slots per week:', template.schedule.length);
        console.log('- Expected total slots:', template.schedule.length * Math.ceil((moment(endDate).diff(moment(startDate), 'days') + 1) / 7));
        
        const generationResponse = await weeklyAvailabilityService.generateTimeSlotsFromTemplate({
          startDate,
          endDate,
          deleteExisting: false // We already deleted manually above
        });
        
        console.log('🎯 === CRITICAL: WHAT BACKEND RETURNED ===');
        console.log('- Backend response:', generationResponse);
        console.log('- Slots generated by backend:', generationResponse.data?.timeSlots?.length || 0);
        if (generationResponse.data?.timeSlots) {
          const slotsByWeek = {};
          generationResponse.data.timeSlots.forEach(slot => {
            const weekKey = moment(slot.date).startOf('week').format('YYYY-MM-DD');
            if (!slotsByWeek[weekKey]) slotsByWeek[weekKey] = [];
            slotsByWeek[weekKey].push(slot);
          });
          console.log('- Slots grouped by week:', slotsByWeek);
          console.log('- ACTUAL weeks generated:', Object.keys(slotsByWeek).length);
          console.log('- Week breakdown:', Object.keys(slotsByWeek).map(week => ({
            week,
            slotsCount: slotsByWeek[week].length
          })));
        }
        
        console.log('🔧 === GENERATION PHASE COMPLETE ===');
        
        if (generationResponse.success) {
          console.log('🔍 === DEBUGGING GENERATION RESPONSE ===');
          console.log('Generation response data:', generationResponse.data);
          console.log('TimeSlots array:', generationResponse.data.timeSlots);
          console.log('TimeSlots length:', generationResponse.data.timeSlots.length);
          console.log('Actual slots created:', generationResponse.data.timeSlots.map(slot => ({
            id: slot._id,
            date: slot.date,
            time: `${slot.startTime}-${slot.endTime}`,
            generatedFrom: slot.generatedFrom
          })));
          
          // Calculate unique slots to get the real count
          const uniqueSlots = new Set(generationResponse.data.timeSlots.map(s => `${s.date}-${s.startTime}-${s.endTime}`));
          const actualSlotCount = uniqueSlots.size;
          
          console.log('Expected vs Actual:', {
            templateScheduleLength: template.schedule.length,
            reportedSlotsGenerated: generationResponse.data.timeSlots.length,
            actualUniqueSlots: actualSlotCount,
            hasDuplicates: generationResponse.data.timeSlots.length !== actualSlotCount
          });
          console.log('======================================');
          
          // Reset change tracking after successful save
          setHasUnsavedChanges(false);
          
          // Track slots before reload
          const slotsBeforeReload = events.filter(e => e.resource?.isIndividual).length;
          console.log(`📊 Slots before reload: ${slotsBeforeReload}`);
          
          // Add delay before reload to ensure database has processed all template generations
          console.log('⏳ Waiting for database to process all TimeSlots...');
          await new Promise(resolve => setTimeout(resolve, 2000)); // Increased to 2 seconds
          
          // FINAL VERIFICATION: Check what's actually in database after generation
          console.log('🔍 FINAL VERIFICATION: What\'s actually in database now...');
          const finalResponse = await timeSlotService.getMyTimeSlots({
            startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
            endDate: moment().add(2, 'months').format('YYYY-MM-DD')
          });
          
          if (finalResponse.success && finalResponse.data?.timeSlots) {
            const finalSlots = finalResponse.data.timeSlots;
            console.log(`🔍 FINAL: ${finalSlots.length} total slots now in database`);
            const slotsByWeek = {};
            finalSlots.forEach(slot => {
              const weekKey = moment(slot.date).startOf('week').format('YYYY-MM-DD');
              if (!slotsByWeek[weekKey]) slotsByWeek[weekKey] = [];
              slotsByWeek[weekKey].push(slot);
            });
            console.log('🔍 FINAL: All slots by week:', Object.keys(slotsByWeek).map(week => ({
              week,
              count: slotsByWeek[week].length,
              times: [...new Set(slotsByWeek[week].map(s => s.startTime + '-' + s.endTime))]
            })));
            
            // HYPOTHESIS TEST: Does the backend generate exactly what we expect?
            const actualWeeks = Object.keys(slotsByWeek).length;
            const expectedWeeks = Math.ceil((moment(endDate).diff(moment(startDate), 'days') + 1) / 7);
            console.log('🧪 HYPOTHESIS TEST:');
            console.log(`- Expected weeks based on our date range: ${expectedWeeks}`);
            console.log(`- Actual weeks generated by backend: ${actualWeeks}`);
            console.log(`- Match: ${actualWeeks === expectedWeeks ? '✅ YES' : '❌ NO'}`);
            
            if (actualWeeks !== expectedWeeks) {
              console.warn('🚨 HYPOTHESIS CONFIRMED: Backend is ignoring our date range and using its own logic!');
              console.warn('🚨 This suggests the backend /generate-slots endpoint has a bug or uses different parameters');
            }
          }
          
          // Reload to show the new generated slots
          console.log('🔄 Reloading availability after template generation...');
          await loadWeeklyAvailability();
          
          // Add additional delay to ensure state is updated
          setTimeout(() => {
            const currentSlots = events.filter(e => e.resource?.isIndividual).length;
            const newlyAddedSlots = Math.max(0, currentSlots - slotsBeforeReload);
            
            console.log(`📊 Final verification - Slots after reload: ${currentSlots}, newly added: ${newlyAddedSlots}`);
            console.log(`📊 Events now showing:`, events.map(e => ({
              id: e.id,
              isIndividual: e.resource?.isIndividual,
              isTemplate: e.resource?.isTemplate,
              title: e.title
            })));
            
            if (newlyAddedSlots > 0) {
              setSuccess(`Weekly schedule saved and ${newlyAddedSlots} new time slots added!`);
            } else {
              setSuccess(`Weekly schedule saved successfully!`);
            }
          }, 500); // Increased delay to ensure state is fully updated
          
        } else {
          setSuccess('Weekly schedule saved but failed to generate time slots');
        }
      } else {
        console.error('Template save failed:', templateResponse);
        setError(`Failed to save weekly template: ${templateResponse.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ === CRITICAL ERROR IN SAVE OPERATION ===');
      console.error('❌ Error type:', error.constructor.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error response status:', error.response?.status);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Server error details:', error.response?.data?.error);
      console.error('❌ Server error message:', error.response?.data?.error?.message);
      console.error('❌ Server error type:', error.response?.data?.error?.type);
      console.error('❌ Server validation info:', error.response?.data?.error?.validation);
      console.error('❌ Full error object:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Current events when error occurred:', events);
      console.error('❌ Current template when error occurred:', weeklyTemplate);
      console.error('❌ ============================================');
      
      let errorMessage = 'Failed to save weekly schedule';
      if (error.response?.data?.error?.message) {
        errorMessage += ': ' + error.response.data.error.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Clear this week's events only
  const handleClearThisWeek = async () => {
    try {
      setLoading(true);
      console.log('🗑️ Clearing this week\'s time slots from database...');
      
      // Save current state for undo
      setLastActionEvents([...events]);
      
      // Get current week's date range
      const startDate = moment().startOf('week').format('YYYY-MM-DD');
      const endDate = moment().endOf('week').format('YYYY-MM-DD');
      
      // Get all existing TimeSlots in this week only
      const existingResponse = await timeSlotService.getMyTimeSlots({
        startDate,
        endDate
      });
      
      if (existingResponse.success && existingResponse.data?.timeSlots) {
        console.log(`🗑️ Found ${existingResponse.data.timeSlots.length} TimeSlots to delete this week`);
        
        // Delete each TimeSlot from database
        for (const slot of existingResponse.data.timeSlots) {
          try {
            await timeSlotService.deleteTimeSlot(slot._id);
            console.log(`✅ Deleted TimeSlot ${slot._id}`);
          } catch (deleteError) {
            console.error(`❌ Failed to delete TimeSlot ${slot._id}:`, deleteError);
          }
        }
      }
      
      // Filter out this week's events from UI
      const thisWeekStart = moment().startOf('week');
      const thisWeekEnd = moment().endOf('week');
      const filteredEvents = events.filter(event => {
        const eventDate = moment(event.start);
        return !eventDate.isBetween(thisWeekStart, thisWeekEnd, 'day', '[]');
      });
      
      setEvents(filteredEvents);
      setSuccess('This week\'s time slots cleared from database');
      
    } catch (error) {
      console.error('Error clearing this week\'s time slots:', error);
      setError('Failed to clear some time slots for this week');
    } finally {
      setLoading(false);
    }
  };

  // Clear all events
  const handleClearAll = async () => {
    try {
      setLoading(true);
      console.log('🗑️ Clearing all time slots from database...');
      
      // Save current state for undo
      setLastActionEvents([...events]);
      
      // Get expanded date range to match load range
      const startDate = moment().startOf('week').subtract(1, 'week').format('YYYY-MM-DD');
      const endDate = moment().startOf('week').add(8, 'weeks').format('YYYY-MM-DD');
      
      // Get all existing TimeSlots in the range
      const existingResponse = await timeSlotService.getMyTimeSlots({
        startDate,
        endDate
      });
      
      if (existingResponse.success && existingResponse.data?.timeSlots) {
        console.log(`🗑️ Found ${existingResponse.data.timeSlots.length} TimeSlots to delete`);
        
        // Delete each TimeSlot from database
        for (const slot of existingResponse.data.timeSlots) {
          try {
            await timeSlotService.deleteTimeSlot(slot._id);
            console.log(`✅ Deleted TimeSlot ${slot._id}`);
          } catch (deleteError) {
            console.error(`❌ Failed to delete TimeSlot ${slot._id}:`, deleteError);
          }
        }
      }
      
      // Also clear the weekly template to start fresh
      if (weeklyTemplate && weeklyTemplate.schedule && weeklyTemplate.schedule.length > 0) {
        console.log('🗑️ Clearing weekly template...');
        const emptyTemplate = {
          ...weeklyTemplate,
          schedule: []
        };
        
        try {
          await weeklyAvailabilityService.saveWeeklyTemplate(emptyTemplate);
          console.log('✅ Weekly template cleared');
        } catch (templateError) {
          console.error('❌ Failed to clear weekly template:', templateError);
        }
      }
      
      // Clear UI
      setEvents([]);
      setOriginalEvents([]);
      setHasUnsavedChanges(false);
      setSuccess('All time slots and template cleared from database');
      
    } catch (error) {
      console.error('Error clearing all time slots:', error);
      setError('Failed to clear some time slots');
    } finally {
      setLoading(false);
    }
  };

  // Check if events have changed since last save
  const checkForChanges = useCallback((currentEvents, originalEvents) => {
    if (currentEvents.length !== originalEvents.length) {
      return true;
    }
    
    // Create comparable event signatures
    const currentSignatures = currentEvents.map(e => ({
      id: e.id,
      start: e.start?.getTime(),
      end: e.end?.getTime(),
      isNew: e.resource?.isNew,
      isTemplate: e.resource?.isTemplate,
      isIndividual: e.resource?.isIndividual
    })).sort((a, b) => a.id.localeCompare(b.id));
    
    const originalSignatures = originalEvents.map(e => ({
      id: e.id,
      start: e.start?.getTime(),
      end: e.end?.getTime(),
      isNew: e.resource?.isNew,
      isTemplate: e.resource?.isTemplate,
      isIndividual: e.resource?.isIndividual
    })).sort((a, b) => a.id.localeCompare(b.id));
    
    return JSON.stringify(currentSignatures) !== JSON.stringify(originalSignatures);
  }, []);

  // Update change tracking when events change
  useEffect(() => {
    const hasChanges = checkForChanges(events, originalEvents);
    setHasUnsavedChanges(hasChanges);
  }, [events, originalEvents, checkForChanges]);

  // REMOVED: eventStyleGetter was never being used by the calendar
  // All styling logic has been moved to eventPropGetter above

  // Handle event drag and drop (moving events)
  const handleEventDrop = ({ event, start, end }) => {
    // Don't allow moving booked individual slots
    if (event.resource.isIndividual && event.resource.status === 'booked') {
      setError('Cannot move booked time slots');
      return;
    }

    const dayName = moment(start).format('dddd').toLowerCase();
    const startTime = moment(start).format('HH:mm');
    const endTime = moment(end).format('HH:mm');

    // Update the event
    const updatedEvent = {
      ...event,
      start,
      end,
      title: `${startTime} - ${endTime}`, // Show time
      resource: {
        ...event.resource,
        day: dayName,
        startTime,
        endTime
      }
    };

    setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
    setSuccess(`Moved slot to ${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${startTime}-${endTime}`);
  };

  // Handle event resize (extending/shrinking duration)
  const handleEventResize = ({ event, start, end }) => {
    // Don't allow resizing booked individual slots
    if (event.resource.isIndividual && event.resource.status === 'booked') {
      setError('Cannot resize booked time slots');
      return;
    }

    // Validate minimum duration (15 minutes)
    const duration = moment(end).diff(moment(start), 'minutes');
    if (duration < 15) {
      setError('Minimum time slot duration is 15 minutes');
      return;
    }

    // Validate maximum duration (8 hours)
    if (duration > 480) {
      setError('Maximum time slot duration is 8 hours');
      return;
    }

    const startTime = moment(start).format('HH:mm');
    const endTime = moment(end).format('HH:mm');

    // Update the event
    const updatedEvent = {
      ...event,
      start,
      end,
      title: `${startTime} - ${endTime}`, // Show time
      resource: {
        ...event.resource,
        startTime,
        endTime
      }
    };

    setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
    setSuccess(`Resized slot to ${startTime}-${endTime} (${duration} minutes)`);
  };

  // FIXED: Consolidated event styling with correct color logic
  const eventPropGetter = (event) => {
    const isTemplate = event.resource?.isTemplate;
    const isIndividual = event.resource?.isIndividual;
    const isRecurring = event.resource?.isRecurring;
    const isBooked = event.resource?.status === 'booked';
    const isFromTemplate = event.resource?.recurringGroup || event.title?.includes('🔁');
    const isNew = event.resource?.isNew;
    
    let backgroundColor = '#4caf50'; // Default GREEN for available slots
    let opacity = 0.8;
    let borderStyle = '0px';
    let cursor = 'grab';
    
    console.log(`🎨 STYLING EVENT: ${event.id}`, {
      title: event.title,
      isTemplate,
      isIndividual, 
      isRecurring,
      isBooked,
      isFromTemplate,
      isNew,
      status: event.resource?.status
    });
    
    // PRIORITY 1: Booked slots are always RED
    if (isBooked) {
      backgroundColor = '#f44336'; // Red for booked
      opacity = 0.9;
      cursor = 'not-allowed';
      console.log(`🎨 → BOOKED SLOT (RED): ${event.id}`);
    }
    // PRIORITY 2A: Recurring series slots are GREEN (same as other available slots)
    else if (isIndividual && !isBooked && isRecurring && event.resource?.recurringSeriesId) {
      backgroundColor = '#4caf50'; // GREEN for recurring series (same as other available slots)
      opacity = 0.8;
      borderStyle = '0px'; // No border - same as regular available slots
      cursor = 'grab';
      console.log(`🎨 → RECURRING SERIES (GREEN): ${event.id}`);
    }
    // PRIORITY 2B: Available individual slots are GREEN (saved slots)
    else if (isIndividual && !isBooked) {
      backgroundColor = '#4caf50'; // GREEN for available individual slots
      opacity = 0.8;
      borderStyle = '0px';
      cursor = 'grab';
      console.log(`🎨 → INDIVIDUAL AVAILABLE (GREEN): ${event.id}`);
    }
    // PRIORITY 3: New unsaved slots are GREEN
    else if (isNew) {
      backgroundColor = '#4caf50'; // GREEN for new slots
      opacity = 0.8;
      borderStyle = '0px';
      cursor = 'grab';
      console.log(`🎨 → NEW UNSAVED SLOT (GREEN): ${event.id}`);
    }
    // PRIORITY 4: Template patterns are BLUE with dashed border (only before save)
    else if (isTemplate && !isIndividual) {
      backgroundColor = '#1976d2';
      opacity = 0.6;
      borderStyle = '2px dashed #ffffff';
      cursor = 'grab';
      console.log(`🎨 → TEMPLATE PATTERN (BLUE DASHED): ${event.id}`);
    }
    // PRIORITY 5: Fallback - all available slots default to GREEN
    else {
      backgroundColor = '#4caf50'; // Default GREEN for all available slots
      opacity = 0.8;
      borderStyle = '0px';
      cursor = 'grab';
      console.log(`🎨 → FALLBACK AVAILABLE (GREEN): ${event.id} - defaulting to green`);
    }
    
    console.log(`🎨 FINAL STYLING FOR ${event.id}:`, {
      backgroundColor,
      opacity,
      borderStyle,
      expectedColor: backgroundColor === '#4caf50' ? 'GREEN (Available)' : backgroundColor === '#f44336' ? 'RED (Booked)' : 'BLUE (Template)'
    });
    
    return {
      className: 'custom-calendar-event',
      style: {
        backgroundColor: backgroundColor,
        borderRadius: '4px',
        opacity,
        color: 'white',
        border: borderStyle,
        display: 'block',
        cursor: cursor,
        // Force override with multiple CSS properties
        backgroundImage: 'none',
        background: backgroundColor
      }
    };
  };

  // Determine drag and resize permissions
  const eventResizable = (event) => {
    // Don't allow resizing of booked individual slots
    if (event.resource.isIndividual && event.resource.status === 'booked') {
      return false;
    }
    return true;
  };

  const eventDraggable = (event) => {
    // Don't allow dragging of booked individual slots
    if (event.resource.isIndividual && event.resource.status === 'booked') {
      return false;
    }
    return true;
  };

  // Close dialog
  const handleCloseDialog = () => {
    // If there's a selected event that is new and unsaved, remove it from the calendar
    if (selectedEvent && selectedEvent.resource?.isNew) {
      console.log('🗑️ Removing unsaved new slot on cancel:', selectedEvent.id);
      setEvents(prev => prev.filter(event => event.id !== selectedEvent.id));
    }
    
    setShowEventDialog(false);
    setShowRecurringOptionsDialog(false);
    setSelectedEvent(null);
    setIsEditing(false);
    setRecurringAction(null);
  };

  // Render mobile view for small screens
  if (isMobile) {
    return <MobileAvailabilityView />;
  }

  // Render desktop view for larger screens
  return (
    <Box>
      {/* Consolidated Actions and Status - Single Row */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Status Information */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 'fit-content' }}>
            <Chip
              label={`${events.length} Slots`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${Math.round(events.reduce((acc, event) => {
                const duration = moment(event.end).diff(moment(event.start), 'minutes');
                return acc + duration;
              }, 0) / 60 * 10) / 10}h/week`}
              color="secondary"
              variant="outlined"
              size="small"
            />
          </Box>
          
          {/* Timezone Selector */}
          <TextField
            select
            size="small"
            value={customTimezone || weeklyTemplate.timezone}
            onChange={(e) => {
              setCustomTimezone(e.target.value);
              setWeeklyTemplate(prev => ({ ...prev, timezone: e.target.value }));
            }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 160, maxWidth: 200 }}
          >
            <option value={Intl.DateTimeFormat().resolvedOptions().timeZone}>
              {Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop()} (Auto)
            </option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Los_Angeles">Pacific</option>
            <option value="Europe/London">London</option>
            <option value="Europe/Paris">Paris</option>
            <option value="Europe/Berlin">Berlin</option>
            <option value="Asia/Tokyo">Tokyo</option>
            <option value="Asia/Shanghai">Shanghai</option>
            <option value="Australia/Sydney">Sydney</option>
          </TextField>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadWeeklyAvailability}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                // Save current state for redo before undoing
                setRedoEvents([...events]);
                setEvents([...lastActionEvents]);
                setLastActionEvents([]); // Clear undo after using it
                setSuccess('Last action undone');
              }}
              disabled={loading || lastActionEvents.length === 0}
              size="small"
            >
              Undo
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={() => {
                // Save current state for undo before redoing
                setLastActionEvents([...events]);
                setEvents([...redoEvents]);
                setRedoEvents([]); // Clear redo after using it
                setSuccess('Last action redone');
              }}
              disabled={loading || redoEvents.length === 0}
              size="small"
            >
              Redo
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearThisWeek}
              disabled={loading || events.length === 0}
              size="small"
            >
              Clear Week
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleClearAll}
              disabled={events.length === 0}
              size="small"
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSchedule}
              disabled={loading || events.length === 0 || !hasUnsavedChanges}
              size="small"
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Fixed Position Alerts at Top */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 400,
            boxShadow: 3
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 400,
            boxShadow: 3
          }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Informational Alert about slot display */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Available blocks are shown as solid green blocks. Booked slots are displayed in 30-minute intervals as red blocks
          and are subtracted from available time. Any available time less than 30 minutes is automatically removed.
        </Typography>
      </Alert>

      {/* Color Legend */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{
            width: 14,
            height: 14,
            backgroundColor: '#1976d2',
            borderRadius: 1,
            border: '1px dashed #ffffff',
            opacity: 0.6
          }} />
          <Typography variant="body2" sx={{ fontSize: '14px' }}>Template Pattern</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, backgroundColor: '#4caf50', borderRadius: 1 }} />
          <Typography variant="body2" sx={{ fontSize: '14px' }}>Available Slots</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, backgroundColor: '#f44336', borderRadius: 1 }} />
          <Typography variant="body2" sx={{ fontSize: '14px' }}>Booked</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '16px' }}>🔁</Typography>
          <Typography variant="body2" sx={{ fontSize: '14px' }}>Recurring Series</Typography>
        </Box>
      </Box>

      {/* Calendar */}
      <Paper sx={{ p: 2 }}>
        <div className="calendar-container">
          <DragAndDropCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            view="week"
            views={['week']}
            selectable="ignoreEvents"
            longPressThreshold={1}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            draggableAccessor={eventDraggable}
            resizableAccessor={eventResizable}
            eventPropGetter={eventPropGetter}
            step={15}
            timeslots={4}
            min={moment().hour(0).minute(0).toDate()}
            max={moment().hour(23).minute(59).toDate()}
            formats={{
              timeGutterFormat: 'HH:mm',
              eventTimeRangeFormat: () => '' // Don't show time range - already in event title
            }}
          />
        </div>
      </Paper>

      {/* Recurring Event Options Dialog */}
      <Dialog 
        open={showRecurringOptionsDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RepeatIcon color="primary" />
            Edit Recurring Event
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            This is a recurring time slot. What would you like to do?
          </Typography>
          
          <List sx={{ width: '100%' }}>
            <ListItem 
              button 
              onClick={() => handleRecurringOptionSelect('single')}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <EventIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Edit this event only"
                secondary="Changes will only affect this single instance"
              />
            </ListItem>

            <ListItem 
              button 
              onClick={() => handleRecurringOptionSelect('series')}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <SeriesIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Edit entire series"
                secondary="Changes will affect all events in this recurring series"
              />
            </ListItem>

            <Divider sx={{ my: 1 }} />

            <ListItem 
              button 
              onClick={() => {
                if (selectedEvent?.resource?.recurringSeriesId) {
                  handleDeleteRecurringSeries(selectedEvent.resource.recurringSeriesId);
                  setShowRecurringOptionsDialog(false);
                } else {
                  handleRecurringOptionSelect('remaining');
                }
              }}
              sx={{ 
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'error.light',
                  '& .MuiListItemText-primary': {
                    color: 'error.contrastText'
                  },
                  '& .MuiListItemText-secondary': {
                    color: 'error.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon>
                <CancelIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Delete entire series"
                secondary={selectedEvent?.resource?.recurringSeriesId 
                  ? `Delete all ${selectedEvent.resource.recurringTotalWeeks} weeks (${selectedEvent.resource.recurringPattern}) of this recurring series`
                  : "Cancel this and all future events in the series"
                }
                sx={{
                  '& .MuiListItemText-primary': {
                    color: 'error.main'
                  }
                }}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Dialog */}
      <Dialog 
        open={showEventDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing 
            ? 'New Time Slot' 
            : recurringAction === 'single' 
              ? 'Edit Single Event'
              : recurringAction === 'series'
                ? 'Edit Entire Series'
                : 'Edit Time Slot'
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={eventForm.startTime}
                  onChange={(e) => setEventForm(prev => ({
                    ...prev,
                    startTime: e.target.value
                  }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Time"
                  type="time"
                  value={eventForm.endTime}
                  onChange={(e) => setEventForm(prev => ({
                    ...prev,
                    endTime: e.target.value
                  }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Rate Override (£/hour)"
              type="number"
              value={eventForm.rateOverride}
              onChange={(e) => setEventForm(prev => ({
                ...prev,
                rateOverride: e.target.value
              }))}
              fullWidth
              placeholder="Leave empty to use default rate"
              inputProps={{ min: 0, step: 0.01 }}
            />

            {/* Days of Week Selector - Always visible for multi-day slot creation */}
            <Box sx={{ mt: 2 }}>
              <FormLabel component="legend">Select Days of Week</FormLabel>
              <FormGroup row sx={{ mt: 1 }}>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={eventForm.selectedDays.includes(day.toLowerCase())}
                        onChange={(e) => {
                          const dayLower = day.toLowerCase();
                          setEventForm(prev => ({
                            ...prev,
                            selectedDays: e.target.checked
                              ? [...prev.selectedDays, dayLower]
                              : prev.selectedDays.filter(d => d !== dayLower)
                          }));
                        }}
                      />
                    }
                    label={day.slice(0, 3)} // Show abbreviated day names
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Select multiple days to create the same time slot across different weekdays
              </Typography>
            </Box>

            {/* Recurring Options */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={eventForm.isRecurring}
                    onChange={(e) => setEventForm(prev => ({
                      ...prev,
                      isRecurring: e.target.checked
                    }))}
                  />
                }
                label="Make this recurring (repeat over multiple weeks)"
              />

              {eventForm.isRecurring && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid #1976d2' }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Recurring Options</FormLabel>
                    <RadioGroup
                      value={eventForm.recurringType}
                      onChange={(e) => setEventForm(prev => ({
                        ...prev,
                        recurringType: e.target.value
                      }))}
                    >
                      <FormControlLabel
                        value="weeks"
                        control={<Radio />}
                        label="Until end date"
                      />
                      <FormControlLabel
                        value="occurrences"
                        control={<Radio />}
                        label="Number of weeks"
                      />
                    </RadioGroup>
                  </FormControl>

                  {eventForm.recurringType === 'weeks' && (
                    <TextField
                      label="End Date"
                      type="date"
                      value={eventForm.recurringEndDate}
                      onChange={(e) => setEventForm(prev => ({
                        ...prev,
                        recurringEndDate: e.target.value
                      }))}
                      fullWidth
                      sx={{ mt: 2 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: moment().format('YYYY-MM-DD')
                      }}
                    />
                  )}

                  {eventForm.recurringType === 'occurrences' && (
                    <TextField
                      label="Number of Weeks"
                      type="number"
                      value={eventForm.recurringOccurrences}
                      onChange={(e) => setEventForm(prev => ({
                        ...prev,
                        recurringOccurrences: parseInt(e.target.value) || 1 // Ensure it's always a number
                      }))}
                      fullWidth
                      sx={{ mt: 2 }}
                      inputProps={{ min: 1, max: 52 }}
                      helperText="How many weeks to repeat this time slot"
                    />
                  )}
                </Box>
              )}
            </Box>

            {selectedEvent && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.100', 
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="body2">
                  <strong>Day:</strong> {selectedEvent.resource.day} <br />
                  <strong>Duration:</strong> {moment(selectedEvent.end).diff(moment(selectedEvent.start), 'minutes')} minutes
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteEvent}
                  size="small"
                >
                  Delete
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEvent}
            variant="contained"
            disabled={
              !eventForm.startTime ||
              !eventForm.endTime ||
              eventForm.selectedDays.length === 0 ||
              (eventForm.isRecurring && eventForm.recurringType === 'weeks' && !eventForm.recurringEndDate) ||
              (eventForm.isRecurring && eventForm.recurringType === 'occurrences' && !eventForm.recurringOccurrences)
            }
          >
            {recurringAction === 'single'
              ? 'Update Single Event'
              : recurringAction === 'series'
                ? 'Update Entire Series'
                : eventForm.isRecurring
                  ? (eventForm.selectedDays.length > 1
                      ? `Create Recurring on ${eventForm.selectedDays.length} Days`
                      : eventForm.recurringType === 'weeks'
                        ? 'Create Recurring (Until End Date)'
                        : `Create ${eventForm.recurringOccurrences} Weeks`)
                  : eventForm.selectedDays.length > 1
                    ? `Create on ${eventForm.selectedDays.length} Days`
                    : (isEditing ? 'Create' : 'Update')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeeklyAvailabilityManager;